import { useState, useEffect, useRef, useCallback } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from "recharts";

// ─── CONFIG ───────────────────────────────────────────────────────────────────
const API_BASE = "https://monitor.midlannex.co.th/api";
// Note: Hanic IoT credentials are handled server-side only — never exposed here

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const C = {
  navy:"#2B2D8B", navyD:"#1E1F6B", navyL:"#3D3FAA",
  white:"#FFFFFF", offwhite:"#F7F8FC",
  slate:"#64748B", slateL:"#94A3B8", border:"#E2E8F0",
  green:"#10B981", amber:"#F59E0B", red:"#EF4444",
  redL:"#FEF2F2", amberL:"#FFFBEB", greenL:"#F0FDF4",
  text:"#0F172A", textM:"#334155",
};

// ─── TRANSLATIONS ─────────────────────────────────────────────────────────────
const T = {
  en: {
    // Brand
    companyShort: "MIDLANNEX",
    companySub: "CO., LTD.",
    platformName: "Water Quality Monitoring Platform",

    // Nav
    navDashboard: "Live Dashboard",
    navHistory: "History & Trends",
    navAlarms: "Alarm Records",
    navReports: "Reports",
    navSettings: "Alert Settings",
    navAdmin: "Admin Panel",

    // Login
    signIn: "Sign In",
    signInSub: "Access your monitoring dashboard",
    emailLabel: "Email Address",
    emailPlaceholder: "you@company.com",
    passwordLabel: "Password",
    securityLabel: "Security Verification",
    captchaPlaceholder: "Type code",
    captchaVerified: "✓ Verified",
    signingIn: "Signing in…",
    needHelp: "Need help?",
    lineSupport: "LINE Support ↗",
    lineSupportFull: "Contact us via LINE Official",
    contactEmail: "report@midlannex.co.th",
    website: "midlannex.co.th",
    footerTagline: "Professional Water Quality Monitoring for Enterprise",
    footerContact: "Need help?",
    footerPrivacy: "All data is encrypted and securely stored.",
    version: "v2.0",
    copyright: `© ${new Date().getFullYear()} Midlannex Co., Ltd. All rights reserved.`,
    invalidCredentials: "Invalid email or password. Please try again.",
    completeCaptcha: "Please complete the security verification.",

    // Suspended
    suspendedTitle: "Service Access Suspended",
    suspendedBody1: "Your subscription for",
    suspendedBody2: "has been placed on hold pending account renewal. To restore full access to your water quality monitoring dashboard, please settle your outstanding balance at your earliest convenience.",
    suspendedBody3: "We value your continued partnership and look forward to resuming service for you.",
    subscriptionExpired: "SUBSCRIPTION EXPIRED",
    expired: "Expired:",
    contactLine: "Contact via LINE to Renew",
    signOut: "Sign out",
    logOut: "Log Out",
    maxDevices: "Max Devices",
    activeSessions: "Active Sessions",
    deviceKicked: "Notice: One device was signed out",
    deviceKickedSub: "You have reached the device limit. The oldest inactive device has been signed out to allow this login.",
    sessionDevice: "Device",
    sessionLastSeen: "Last seen",
    sessionSignOut: "Sign out this device",
    currentSession: "This device",
    noSessions: "No active sessions found.",
    deviceLimit: "Device Limit",
    sessionsTitle: "Active Devices",
    sessionsSub: "Devices currently signed in to your account",
    continueLogin: "Continue",
    understood: "Understood",

    // Topbar
    devicesOnline: "online",
    activeAlerts: "Alert",
    activeAlertsPlural: "Alerts",

    // API Banner
    liveConnected: "Connected to Midlannex Monitoring Platform — live sensor data",
    demoMode: "Demo mode — API unreachable (CORS / network). Showing simulated data. Deploy with a backend proxy to enable live feed.",
    connectingMsg: "Connecting to sensor network…",

    // Dashboard
    liveDashboard: "Live Dashboard",
    liveDashboardSub: "gateway",
    gatewaysSub: "gateways",
    hanicPlatform: "Midlannex Monitoring Platform",
    device: "Device",
    gateways: "GATEWAYS",
    offlineWarning: "Gateway is offline. Displaying last known readings.",
    lastUpdate: "Last:",
    liveLabel: "Live",
    demoLabel: "Demo mode",
    combinedChart: "Combined Live Chart",
    combinedChartSub: "Tap a tile to show/hide · updates every 10s",
    rawValues: "Raw",
    normalised: "Normalised %",
    shownOnChart: "✓ ON CHART",
    hiddenTap: "HIDDEN — tap to show",
    scene: "Scene:",
    idLabel: "ID:",
    codeLabel: "Code:",

    // Sensor labels
    sensorPH: "pH Level",
    sensorTemp: "Temperature",
    sensorTurb: "Turbidity",
    sensorClO2: "Chlorine Dioxide",
    sensorFCL: "Free/Residual Chlorine",
    sensorDO2: "Dissolved O₂",
    sensorCond: "Conductivity",
    sensorSludge: "Sludge Level",
    chlorineModeLabel: "Chlorine channel is displaying:",

    // Status
    statusNormal: "NORMAL",
    statusLow: "LOW",
    statusHigh: "HIGH",
    statusOffline: "OFFLINE",

    // Alerts
    alertsTitle: "Active Alerts",
    deviceOfflineAlert: "Device is OFFLINE",

    // History
    historyTitle: "History & Trends",
    readingLog: "Reading Log",
    range: "RANGE:",
    intervalLabel: "INTERVAL:",
    refresh: "↺ Refresh",
    pickDay: "or pick a day:",
    clearDay: "✕ Back to quick ranges",
    dateTimeCol: "Date & Time",
    offlineBannerTitle: "⚠ Device was offline during this period",
    offlinePeriodOngoing: "offline since {time} (ongoing)",
    offlinePeriodRange: "offline {start} → {end} ({dur})",
    requestReportBtn: "📄 Request PDF Report",
    requestReportTitle: "Request a PDF Report",
    requestReportDesc: "Pick a date range and we'll email you a detailed PDF report.",
    reportFromLabel: "From",
    reportToLabel: "To",
    reportEmailLabel: "Send to email",
    reportSendBtn: "Send Report",
    reportCancelBtn: "Cancel",
    reportSending: "Generating and sending your report...",
    reportSent: "✅ Report is on its way to your inbox!",
    reportRangeError: "Please choose both a from and to date, with from before to.",
    noData: "No data available for this period.",
    graphView: "📈 Graph",
    logView: "📋 Log",
    quickRange: "Quick range",
    pickDate: "Pick a date",
    dateCol: "Date",
    timeCol: "Time",
    deviceWentOffline: "⚠ Device went OFFLINE",
    deviceBackOnline: "✓ Device back ONLINE",
    deviceOfflineSince: "⚠ Device has been offline since",
    reportingInterval: "Gateway reports new readings roughly every",

    // Alarms
    alarmsTitle: "Alarm Records",
    alarmsSub: "Historical alerts from all gateways · Last 24 hours",
    noAlarms: "No alarms in the selected period.",
    alarmTime: "Time",
    alarmSensor: "Sensor",
    alarmGateway: "Gateway",
    alarmValue: "Value",
    alarmThreshold: "Threshold",
    alarmLevel: "Level",
    alarmStatus: "Status",
    alarmActive: "Active",
    alarmResolved: "Resolved",

    // Settings
    settingsTitle: "Alert Thresholds",
    settingsSub: "Configure acceptable ranges per parameter. Alerts trigger when any live reading falls outside these bounds.",
    minimum: "Minimum",
    maximum: "Maximum",
    saveChanges: "Save Changes",
    saved: "✓ Saved",
    primaryLabel: "PRIMARY — 1–3 ppm",

    // Reports
    reportsTitle: "Automated Reports",
    reportsSub: "Schedule professional PDF reports with Midlannex branding delivered to your inbox.",
    deliveryEmail: "Delivery Email",
    frequency: "Frequency",
    weekly: "📅 Weekly",
    monthly: "📆 Monthly",
    reportIncludes: "Each report includes:",
    reportItems: [
      "All gateway summaries with live/offline status",
      "ClO₂ compliance trend (target: 1–3 ppm)",
      "pH, Temperature, Turbidity, DO₂, Conductivity charts",
      "Alarm history with severity levels",
      "Min / average / max per sensor per period",
      "Midlannex logo & client company letterhead",
    ],
    activateWeekly: "Activate Weekly Reports",
    activateMonthly: "Activate Monthly Reports",
    reportActivated: "✓ Schedule activated. First report will be sent to",

    // Admin
    adminTitle: "Admin Panel",
    adminSub: "Manage platform accounts, subscriptions and device assignments.",
    accounts: "Accounts",
    gatewaysTab: "Gateways",
    scenesTab: "Scenes",
    addAccount: "+ Add Account",
    newAccount: "New Account",
    fullName: "Full Name",
    company: "Company",
    password: "Password",
    expiry: "Expiry (YYYY-MM-DD)",
    permanent: "Permanent (No Expiry)",
    neverExpires: "Never",
    setPermanent: "Set as Permanent",
    role: "Role",
    roleUser: "User",
    roleAdmin: "Admin",
    createAccount: "Create Account",
    cancel: "Cancel",
    active: "active",
    accountsCount: "accounts",
    user: "User",
    companyCol: "Company",
    roleCol: "Role",
    statusCol: "Status",
    expiryCol: "Expiry",
    actionsCol: "Actions",
    suspend: "Suspend",
    restore: "Restore",
    edit: "Edit",
    remove: "Remove",
    saveChanges: "Save Changes",
    confirmDelete: "Are you sure you want to delete this account? This cannot be undone.",
    editAccount: "Edit Account",
    gatewayCol: "Gateway",
    sceneNameCol: "Scene",
    deviceCodeCol: "Device Code",
    idCol: "ID",
    statusGwCol: "Status",
    sceneNameTbl: "Scene Name",
    descriptionCol: "Description",
  },

  th: {
    // Brand
    companyShort: "มิดแลนเน็กซ์",
    companySub: "จำกัด",
    platformName: "แพลตฟอร์มตรวจสอบคุณภาพน้ำ",

    // Nav
    navDashboard: "แดชบอร์ดสด",
    navHistory: "ประวัติและแนวโน้ม",
    navAlarms: "บันทึกการแจ้งเตือน",
    navReports: "รายงาน",
    navSettings: "ตั้งค่าการแจ้งเตือน",
    navAdmin: "แผงผู้ดูแล",

    // Login
    signIn: "เข้าสู่ระบบ",
    signInSub: "เข้าถึงแดชบอร์ดการตรวจสอบของคุณ",
    emailLabel: "ที่อยู่อีเมล",
    emailPlaceholder: "อีเมล@บริษัท.com",
    passwordLabel: "รหัสผ่าน",
    securityLabel: "การยืนยันความปลอดภัย",
    captchaPlaceholder: "พิมพ์รหัส",
    captchaVerified: "✓ ยืนยันแล้ว",
    signingIn: "กำลังเข้าสู่ระบบ…",
    needHelp: "ต้องการความช่วยเหลือ?",
    lineSupport: "ติดต่อ LINE ↗",
    lineSupportFull: "ติดต่อเราผ่าน LINE Official",
    contactEmail: "report@midlannex.co.th",
    website: "midlannex.co.th",
    footerTagline: "ระบบตรวจสอบคุณภาพน้ำระดับองค์กร",
    footerContact: "ต้องการความช่วยเหลือ?",
    footerPrivacy: "ข้อมูลทั้งหมดถูกเข้ารหัสและจัดเก็บอย่างปลอดภัย",
    version: "v2.0",
    copyright: `© ${new Date().getFullYear() + 543} มิดแลนเน็กซ์ จำกัด สงวนลิขสิทธิ์`,
    invalidCredentials: "อีเมลหรือรหัสผ่านไม่ถูกต้อง กรุณาลองใหม่",
    completeCaptcha: "กรุณายืนยันรหัสความปลอดภัยก่อน",

    // Suspended
    suspendedTitle: "ระงับการเข้าถึงบริการ",
    suspendedBody1: "การสมัครสมาชิกของ",
    suspendedBody2: "ถูกระงับชั่วคราวเนื่องจากรอการต่ออายุ หากต้องการเข้าถึงแดชบอร์ดตรวจสอบคุณภาพน้ำอีกครั้ง กรุณาชำระยอดค้างชำระโดยเร็วที่สุด",
    suspendedBody3: "เราให้ความสำคัญกับความร่วมมือของคุณ และหวังว่าจะได้ให้บริการต่อไปในเร็วๆ นี้",
    subscriptionExpired: "การสมัครสมาชิกหมดอายุ",
    expired: "หมดอายุ:",
    contactLine: "ติดต่อ LINE เพื่อต่ออายุ",
    signOut: "ออกจากระบบ",
    logOut: "ออกจากระบบ",
    maxDevices: "จำนวนอุปกรณ์สูงสุด",
    activeSessions: "อุปกรณ์ที่เข้าสู่ระบบ",
    deviceKicked: "แจ้งเตือน: อุปกรณ์หนึ่งถูกออกจากระบบ",
    deviceKickedSub: "คุณถึงขีดจำกัดจำนวนอุปกรณ์แล้ว อุปกรณ์ที่ไม่ได้ใช้งานนานที่สุดถูกออกจากระบบเพื่อให้คุณเข้าสู่ระบบได้",
    sessionDevice: "อุปกรณ์",
    sessionLastSeen: "ใช้งานล่าสุด",
    sessionSignOut: "ออกจากระบบอุปกรณ์นี้",
    currentSession: "อุปกรณ์นี้",
    noSessions: "ไม่พบการเข้าสู่ระบบที่ใช้งานอยู่",
    deviceLimit: "ขีดจำกัดอุปกรณ์",
    sessionsTitle: "อุปกรณ์ที่ใช้งานอยู่",
    sessionsSub: "อุปกรณ์ที่เข้าสู่ระบบในบัญชีของคุณ",
    continueLogin: "ดำเนินการต่อ",
    understood: "รับทราบ",

    // Topbar
    devicesOnline: "ออนไลน์",
    activeAlerts: "การแจ้งเตือน",
    activeAlertsPlural: "การแจ้งเตือน",

    // API Banner
    liveConnected: "เชื่อมต่อกับแพลตฟอร์ม Midlannex — ข้อมูลเซ็นเซอร์สด",
    demoMode: "โหมดสาธิต — ไม่สามารถเชื่อมต่อ API ได้ แสดงข้อมูลจำลอง",
    connectingMsg: "กำลังเชื่อมต่อเครือข่ายเซ็นเซอร์…",

    // Dashboard
    liveDashboard: "แดชบอร์ดสด",
    liveDashboardSub: "เกตเวย์",
    gatewaysSub: "เกตเวย์",
    hanicPlatform: "แพลตฟอร์ม Midlannex",
    device: "อุปกรณ์",
    gateways: "เกตเวย์",
    offlineWarning: "เกตเวย์ออฟไลน์ กำลังแสดงข้อมูลล่าสุด",
    lastUpdate: "อัปเดตล่าสุด:",
    liveLabel: "สด",
    demoLabel: "โหมดสาธิต",
    combinedChart: "กราฟรวมสด",
    combinedChartSub: "แตะช่องเพื่อแสดง/ซ่อน · อัปเดตทุก 10 วินาที",
    rawValues: "ค่าดิบ",
    normalised: "ค่าปรับมาตรฐาน %",
    shownOnChart: "✓ แสดงในกราฟ",
    hiddenTap: "ซ่อนอยู่ — แตะเพื่อแสดง",
    scene: "โปรเจกต์:",
    idLabel: "รหัส:",
    codeLabel: "โค้ด:",

    // Sensor labels
    sensorPH: "ค่า pH",
    sensorTemp: "อุณหภูมิ",
    sensorTurb: "ความขุ่น",
    sensorClO2: "คลอรีนไดออกไซด์",
    sensorFCL: "คลอรีนอิสระ/คลอรีนคงเหลือ",
    sensorDO2: "ออกซิเจนละลายน้ำ",
    sensorCond: "การนำไฟฟ้า",
    sensorSludge: "ระดับตะกอน",
    chlorineModeLabel: "ช่องคลอรีนกำลังแสดงผลเป็น:",

    // Status
    statusNormal: "ปกติ",
    statusLow: "ต่ำ",
    statusHigh: "สูง",
    statusOffline: "ออฟไลน์",

    // Alerts
    alertsTitle: "การแจ้งเตือนที่ใช้งาน",
    deviceOfflineAlert: "อุปกรณ์ออฟไลน์",

    // History
    historyTitle: "ประวัติและแนวโน้ม",
    readingLog: "บันทึกการอ่านค่า",
    range: "ช่วงเวลา:",
    intervalLabel: "ช่วงห่าง:",
    refresh: "↺ รีเฟรช",
    pickDay: "หรือเลือกวันที่:",
    clearDay: "✕ กลับไปช่วงเวลาด่วน",
    dateTimeCol: "วันที่ & เวลา",
    offlineBannerTitle: "⚠ อุปกรณ์ออฟไลน์ในช่วงเวลานี้",
    offlinePeriodOngoing: "ออฟไลน์ตั้งแต่ {time} (ยังไม่กลับมา)",
    offlinePeriodRange: "ออฟไลน์ {start} → {end} ({dur})",
    requestReportBtn: "📄 ขอรายงาน PDF",
    requestReportTitle: "ขอรายงาน PDF",
    requestReportDesc: "เลือกช่วงวันที่ แล้วเราจะส่งรายงาน PDF ฉบับละเอียดไปให้ทางอีเมล",
    reportFromLabel: "จากวันที่",
    reportToLabel: "ถึงวันที่",
    reportEmailLabel: "ส่งไปที่อีเมล",
    reportSendBtn: "ส่งรายงาน",
    reportCancelBtn: "ยกเลิก",
    reportSending: "กำลังสร้างและส่งรายงานของคุณ...",
    reportSent: "✅ รายงานกำลังส่งไปยังอีเมลของคุณ!",
    reportRangeError: "กรุณาเลือกวันที่เริ่มต้นและสิ้นสุด โดยวันที่เริ่มต้นต้องมาก่อนวันที่สิ้นสุด",
    noData: "ไม่มีข้อมูลสำหรับช่วงเวลานี้",
    graphView: "📈 กราฟ",
    logView: "📋 บันทึก",
    quickRange: "ช่วงเวลาด่วน",
    pickDate: "เลือกวันที่",
    dateCol: "วันที่",
    timeCol: "เวลา",
    deviceWentOffline: "⚠ อุปกรณ์ออฟไลน์",
    deviceBackOnline: "✓ อุปกรณ์กลับมาออนไลน์",
    deviceOfflineSince: "⚠ อุปกรณ์ออฟไลน์ตั้งแต่",
    reportingInterval: "เกตเวย์รายงานค่าใหม่ทุกประมาณ",

    // Alarms
    alarmsTitle: "บันทึกการแจ้งเตือน",
    alarmsSub: "การแจ้งเตือนทั้งหมดจากเกตเวย์ · 24 ชั่วโมงล่าสุด",
    noAlarms: "ไม่มีการแจ้งเตือนในช่วงเวลานี้",
    alarmTime: "เวลา",
    alarmSensor: "เซ็นเซอร์",
    alarmGateway: "เกตเวย์",
    alarmValue: "ค่าที่วัดได้",
    alarmThreshold: "ค่าเกณฑ์",
    alarmLevel: "ระดับ",
    alarmStatus: "สถานะ",
    alarmActive: "แจ้งเตือนอยู่",
    alarmResolved: "แก้ไขแล้ว",

    // Settings
    settingsTitle: "ค่าเกณฑ์การแจ้งเตือน",
    settingsSub: "กำหนดช่วงค่าที่ยอมรับได้ของแต่ละพารามิเตอร์ การแจ้งเตือนจะเกิดขึ้นเมื่อค่าเกินขอบเขต",
    minimum: "ค่าต่ำสุด",
    maximum: "ค่าสูงสุด",
    saveChanges: "บันทึกการเปลี่ยนแปลง",
    saved: "✓ บันทึกแล้ว",
    primaryLabel: "หลัก — 1–3 ppm",

    // Reports
    reportsTitle: "รายงานอัตโนมัติ",
    reportsSub: "ตั้งเวลาส่งรายงาน PDF แบบมืออาชีพพร้อมโลโก้ Midlannex ไปยังอีเมลของคุณ",
    deliveryEmail: "อีเมลรับรายงาน",
    frequency: "ความถี่",
    weekly: "📅 รายสัปดาห์",
    monthly: "📆 รายเดือน",
    reportIncludes: "รายงานประกอบด้วย:",
    reportItems: [
      "สรุปเกตเวย์ทั้งหมดพร้อมสถานะออนไลน์/ออฟไลน์",
      "แนวโน้มความสอดคล้องของ ClO₂ (เป้าหมาย: 1–3 ppm)",
      "กราฟ pH อุณหภูมิ ความขุ่น DO₂ การนำไฟฟ้า",
      "ประวัติการแจ้งเตือนพร้อมระดับความรุนแรง",
      "ค่าต่ำสุด / เฉลี่ย / สูงสุดต่อเซ็นเซอร์ต่อช่วงเวลา",
      "หัวจดหมายโลโก้ Midlannex และบริษัทลูกค้า",
    ],
    activateWeekly: "เปิดใช้งานรายงานรายสัปดาห์",
    activateMonthly: "เปิดใช้งานรายงานรายเดือน",
    reportActivated: "✓ เปิดใช้งานแล้ว รายงานแรกจะถูกส่งไปที่",

    // Admin
    adminTitle: "แผงผู้ดูแลระบบ",
    adminSub: "จัดการบัญชี การสมัครสมาชิก และการกำหนดอุปกรณ์",
    accounts: "บัญชีผู้ใช้",
    gatewaysTab: "เกตเวย์",
    scenesTab: "โปรเจกต์",
    addAccount: "+ เพิ่มบัญชี",
    newAccount: "บัญชีใหม่",
    fullName: "ชื่อเต็ม",
    company: "บริษัท",
    password: "รหัสผ่าน",
    expiry: "วันหมดอายุ (YYYY-MM-DD)",
    permanent: "ถาวร (ไม่มีวันหมดอายุ)",
    neverExpires: "ไม่หมดอายุ",
    setPermanent: "ตั้งเป็นถาวร",
    role: "บทบาท",
    roleUser: "ผู้ใช้",
    roleAdmin: "ผู้ดูแล",
    createAccount: "สร้างบัญชี",
    cancel: "ยกเลิก",
    active: "ใช้งาน",
    accountsCount: "บัญชี",
    user: "ผู้ใช้",
    companyCol: "บริษัท",
    roleCol: "บทบาท",
    statusCol: "สถานะ",
    expiryCol: "วันหมดอายุ",
    actionsCol: "การดำเนินการ",
    suspend: "ระงับ",
    restore: "คืนสิทธิ์",
    edit: "แก้ไข",
    remove: "ลบ",
    saveChanges: "บันทึก",
    confirmDelete: "แน่ใจหรือไม่ว่าต้องการลบบัญชีนี้? ไม่สามารถยกเลิกได้",
    editAccount: "แก้ไขบัญชี",
    gatewayCol: "เกตเวย์",
    sceneNameCol: "โปรเจกต์",
    deviceCodeCol: "รหัสอุปกรณ์",
    idCol: "รหัส",
    statusGwCol: "สถานะ",
    sceneNameTbl: "ชื่อโปรเจกต์",
    descriptionCol: "รายละเอียด",
  },
};

// ─── SENSOR NAME MAP ──────────────────────────────────────────────────────────
const SENSOR_NAME_MAP = {
  // ── Chinese names (original Hanic platform) ───────────────────────────────
  "PH":       { key:"pH",          labelKey:"sensorPH",   short:"pH",    unit:"",      icon:"⚗️",  color:"#0EA5E9", threshMin:6.5, threshMax:8.5 },
  "温度":     { key:"temp",        labelKey:"sensorTemp", short:"Temp",  unit:"°C",    icon:"🌡️", color:"#F97316", threshMin:15,  threshMax:35  },
  "浊度":     { key:"turbidity",   labelKey:"sensorTurb", short:"NTU",   unit:"NTU",   icon:"🌫️", color:"#8B5CF6", threshMin:0,   threshMax:1.0 },
  "余氯":     { key:"clO2",        labelKey:"sensorClO2", short:"ClO₂",  unit:"mg/L",  icon:"🧪",  color:"#6366F1", threshMin:1.0, threshMax:3.0 },
  "溶解氧":   { key:"dissolvedO2", labelKey:"sensorDO2",  short:"DO₂",   unit:"mg/L",  icon:"🫧",  color:"#EC4899", threshMin:5.0, threshMax:12  },
  "电导率":   { key:"conductivity",labelKey:"sensorCond", short:"Cond",  unit:"mS/cm", icon:"⚡",  color:"#10B981", threshMin:0,   threshMax:100 },
  "泥位高度": { key:"sludge",      labelKey:"sensorSludge",short:"Sludge",unit:"m",    icon:"📏",  color:"#78716C", threshMin:0,   threshMax:3.0 },
  // ── English names (some Hanic devices use English) ────────────────────────
  "Residual Chlorine": { key:"clO2",        labelKey:"sensorClO2", short:"ClO₂",  unit:"mg/L",  icon:"🧪",  color:"#6366F1", threshMin:1.0, threshMax:3.0 },
  "Temperature":       { key:"temp",        labelKey:"sensorTemp", short:"Temp",  unit:"°C",    icon:"🌡️", color:"#F97316", threshMin:15,  threshMax:35  },
  "pH":                { key:"pH",          labelKey:"sensorPH",   short:"pH",    unit:"",      icon:"⚗️",  color:"#0EA5E9", threshMin:6.5, threshMax:8.5 },
  "Turbidity":         { key:"turbidity",   labelKey:"sensorTurb", short:"NTU",   unit:"NTU",   icon:"🌫️", color:"#8B5CF6", threshMin:0,   threshMax:1.0 },
  "Dissolved Oxygen":  { key:"dissolvedO2", labelKey:"sensorDO2",  short:"DO₂",   unit:"mg/L",  icon:"🫧",  color:"#EC4899", threshMin:5.0, threshMax:12  },
  "Conductivity":      { key:"conductivity",labelKey:"sensorCond", short:"Cond",  unit:"mS/cm", icon:"⚡",  color:"#10B981", threshMin:0,   threshMax:100 },
  "Sludge Level":      { key:"sludge",      labelKey:"sensorSludge",short:"Sludge",unit:"m",    icon:"📏",  color:"#78716C", threshMin:0,   threshMax:3.0 },
  "Flow Rate":         { key:"flow",        labelKey:"sensorFlow", short:"Flow",  unit:"m³/h",  icon:"💧",  color:"#06B6D4", threshMin:0,   threshMax:10  },
  "Chlorine Dioxide":  { key:"clO2",        labelKey:"sensorClO2", short:"ClO₂",  unit:"mg/L",  icon:"🧪",  color:"#6366F1", threshMin:1.0, threshMax:3.0 },
  "Chlorine":          { key:"clO2",        labelKey:"sensorClO2", short:"ClO₂",  unit:"mg/L",  icon:"🧪",  color:"#6366F1", threshMin:1.0, threshMax:3.0 },
  "DO":                { key:"dissolvedO2", labelKey:"sensorDO2",  short:"DO₂",   unit:"mg/L",  icon:"🫧",  color:"#EC4899", threshMin:5.0, threshMax:12  },
  "ORP":               { key:"conductivity",labelKey:"sensorCond", short:"ORP",   unit:"mV",    icon:"⚡",  color:"#10B981", threshMin:-200,threshMax:800 },
};

const KEY_META = Object.fromEntries(
  Object.values(SENSOR_NAME_MAP).map(m => [m.key, m])
);

const buildDefaultThresholds = () =>
  Object.fromEntries(
    Object.values(SENSOR_NAME_MAP).map(m => [m.key, { min: m.threshMin, max: m.threshMax }])
  );

// ─── CHLORINE DISPLAY MODE (manual ClO2 / FCL toggle) ────────────────────────
// The physical analyzer can be switched between measuring Chlorine Dioxide
// (ClO2) and Free/Residual Chlorine (FCL) — same wire, same "clO2" chart key,
// different meaning. The dashboard can't detect which mode the instrument is
// currently in on its own, so this is a manual per-gateway toggle the operator
// sets to match whatever the physical controller's display currently shows.
// It only changes the LABEL/UNIT/ICON shown — it does not touch the "clO2"
// internal key, so history/thresholds/graph continue working unchanged.
const CHLORINE_DISPLAY_MODES = {
  clO2: { short: "ClO₂", labelKey: "sensorClO2", unit: "mg/L", icon: "🧪" },
  fcl:  { short: "FCL",  labelKey: "sensorFCL",   unit: "mg/L", icon: "🧪" },
};

function getChlorineMode(gatewayId) {
  try {
    return localStorage.getItem(`mlx_clo2_mode_${gatewayId}`) === "fcl" ? "fcl" : "clO2";
  } catch { return "clO2"; }
}
function setChlorineMode(gatewayId, mode) {
  try { localStorage.setItem(`mlx_clo2_mode_${gatewayId}`, mode); } catch {}
}

// Returns display info (short/labelKey/unit/icon) for a sensor, applying the
// chlorine mode override when the sensor's key is "clO2".
function displayMetaFor(sensorMeta, gatewayId, chlorineMode) {
  if (sensorMeta.key === "clO2") {
    return { ...sensorMeta, ...CHLORINE_DISPLAY_MODES[chlorineMode] };
  }
  return sensorMeta;
}

// ─── PLATFORM USERS ───────────────────────────────────────────────────────────
const PLATFORM_USERS = [
  { id:1, email:"admin@midlannex.com",    password:"Admin@2024", role:"admin", name:"Admin",          company:"Midlannex Co., Ltd.",   subscriptionStatus:"active",    subscriptionExpiry:"permanent", sceneIds:[] },
  { id:2, email:"operator@acmecorp.com",  password:"Pass1234",   role:"user",  name:"John Wattana",   company:"ACME Corporation",       subscriptionStatus:"active",    subscriptionExpiry:"2026-09-01", sceneIds:[] },
  { id:3, email:"manager@globaltech.com", password:"Pass1234",   role:"user",  name:"Somchai Prasit", company:"Global Tech Industries", subscriptionStatus:"suspended", subscriptionExpiry:"2026-05-01", sceneIds:[] },
];

// ─── BACKEND API ──────────────────────────────────────────────────────────────
// All calls go through YOUR backend at monitor.midlannex.co.th/api
// The backend handles Hanic credentials server-side — they never reach the browser
const HanicAPI = {
  _h() {
    const h = { "Content-Type":"application/json" };
    if (window._midlannexToken) h["Authorization"] = `Bearer ${window._midlannexToken}`;
    return h;
  },
  // Login is handled separately via /api/auth/login — see LoginPage submit()
  async login() { return { ok: true }; }, // no-op: login done at auth level

  async getScenes() {
    try {
      const r = await fetch(`${API_BASE}/sensors/scenes`, {headers:this._h()});
      if(!r.ok) {
        const errBody = await r.json().catch(()=>({error:`HTTP ${r.status}`}));
        console.warn("getScenes failed:", r.status, errBody);
        return [];
      }
      const d = await r.json();
      console.log("getScenes response:", d);
      return d.scenes || [];
    } catch(err) {
      console.warn("getScenes network error:", err.message);
      return [];
    }
  },
  async getGateways(sceneId) {
    try {
      const r = await fetch(`${API_BASE}/sensors/gateways?sceneId=${sceneId}`, {headers:this._h()});
      if(!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      return d.gateways || [];
    } catch(err) {
      console.warn(`getGateways failed for scene ${sceneId}:`, err.message);
      return [];
    }
  },
  async getSlaveDevices(nodeId) {
    // Handled by getSensors directly — returns dummy to satisfy call chain
    return [{id: nodeId}];
  },
  async getSensors(nodeId, deviceId) {
    try {
      const r = await fetch(`${API_BASE}/sensors/live?gatewayId=${nodeId}`, {headers:this._h()});
      if(!r.ok) {
        const errBody = await r.json().catch(()=>({error:`HTTP ${r.status}`}));
        console.warn(`getSensors HTTP ${r.status} for gateway ${nodeId}:`, errBody);
        return [];
      }
      const d = await r.json();
      console.log(`getSensors gateway ${nodeId}:`, d.sensors?.map(s=>`${s.name}=${s.sdata}`));
      return d.sensors || [];
    } catch(err) {
      console.warn(`getSensors network error for gateway ${nodeId}:`, err.message);
      return [];
    }
  },
  async getHistoryReduced(sensorId,startTime,endTime,interval="1m") {
    try { const r=await fetch(`${API_BASE}/sensors/history?sensorId=${sensorId}&startTime=${encodeURIComponent(startTime)}&endTime=${encodeURIComponent(endTime)}&intervalType=${interval}`,{headers:this._h()}); const d=await r.json(); return d.data||[]; } catch{return[];}
  },
  async getAlarms(params={}) {
    const q=new URLSearchParams(params).toString();
    try { const r=await fetch(`${API_BASE}/sensors/alarms?${q}`,{headers:this._h()}); const d=await r.json(); return d.alarms||[]; } catch{return[];}
  },
  async getLiveSnapshot() {
    try { const r=await fetch(`${API_BASE}/sensors/all-live`,{headers:this._h()}); const d=await r.json(); return d.snapshot||{}; } catch{return{};}
  },
};

// ─── MOCK DATA ────────────────────────────────────────────────────────────────
const rnd = (b,v) => +(b+(Math.random()-0.5)*v*2).toFixed(3);
const MOCK_SENSORS = [
  { id:3966222, name:"PH",      sdata:9.16, measure_unit:"pH",    data:{ iot_sensor_status:"online" } },
  { id:3966223, name:"温度",    sdata:23.7,  measure_unit:"°C",    data:{ iot_sensor_status:"online" } },
  { id:3966224, name:"浊度",    sdata:0.15,  measure_unit:"NTU",   data:{ iot_sensor_status:"online" } },
  { id:3966227, name:"溶解氧",  sdata:4.83,  measure_unit:"mg/L",  data:{ iot_sensor_status:"online" } },
  { id:3966233, name:"电导率",  sdata:39.0,  measure_unit:"mS/cm", data:{ iot_sensor_status:"online" } },
  { id:5924920, name:"泥位高度",sdata:1.83,  measure_unit:"m",     data:{ iot_sensor_status:"online" } },
];
const MOCK_GATEWAYS = [{ id:259535, name:"智能水质监测系统平台", scene_name:"Default Group", device_code:"37C60943H4L2W7A6", data:{ iot_node_status:"online" }, scene_id:1 }];
const MOCK_SCENES   = [{ id:1, name:"默认组", description:"Default monitoring group" }];
const mockTick = ss => ss.map(s=>{ const m=SENSOR_NAME_MAP[s.name]; if(!m)return s; const base=m.threshMin+(m.threshMax-m.threshMin)*0.45; return {...s,sdata:rnd(base,(m.threshMax-m.threshMin)*0.08)}; });

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const tsToTime = ts => new Date(ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit",second:"2-digit"});
const nowStr   = ()  => new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit",second:"2-digit"});

const statusOf = (val,key) => {
  const t = buildDefaultThresholds()[key];
  if (!t || val==null) return "—";
  if (val < t.min) return "LOW";
  if (val > t.max) return "HIGH";
  return "NORMAL";
};
const statusColor = s => ({ NORMAL:C.green, LOW:C.red, HIGH:C.red }[s] || C.slateL);

// ─── UI ATOMS ─────────────────────────────────────────────────────────────────
const LBL = { display:"block", fontSize:11, fontWeight:700, color:C.slate, marginBottom:5, letterSpacing:"0.05em", textTransform:"uppercase" };
const INP = { width:"100%", padding:"10px 12px", border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, color:C.text, background:C.offwhite, boxSizing:"border-box", fontFamily:"inherit", outline:"none" };

const LOGO_SRC = "data:image/jpeg;base64,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";

function Logo({ size=36 }) {
  return (
    <div style={{
      width:size, height:Math.round(size*1.18),
      display:"flex", alignItems:"center", justifyContent:"center",
      overflow:"hidden", flexShrink:0,
    }}>
      <img
        src={LOGO_SRC}
        alt="Midlannex Logo"
        style={{ width:"100%", height:"100%", objectFit:"contain", display:"block" }}
      />
    </div>
  );
}

// ── Language Switcher ─────────────────────────────────────────────────────────
// SVG flag components — reliable cross-platform, no emoji dependency
function FlagUK() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 30" width="22" height="14" style={{borderRadius:2,display:"block"}}>
      <clipPath id="a"><path d="M0 0v30h60V0z"/></clipPath>
      <clipPath id="b"><path d="M30 15h30v15zv15H0zH0V0zV0h30z"/></clipPath>
      <g clipPath="url(#a)">
        <path d="M0 0v30h60V0z" fill="#012169"/>
        <path d="M0 0l60 30m0-30L0 30" stroke="#fff" strokeWidth="6"/>
        <path d="M0 0l60 30m0-30L0 30" clipPath="url(#b)" stroke="#C8102E" strokeWidth="4"/>
        <path d="M30 0v30M0 15h60" stroke="#fff" strokeWidth="10"/>
        <path d="M30 0v30M0 15h60" stroke="#C8102E" strokeWidth="6"/>
      </g>
    </svg>
  );
}

function FlagTH() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 900 600" width="22" height="14" style={{borderRadius:2,display:"block"}}>
      <rect width="900" height="600" fill="#A51931"/>
      <rect width="900" height="400" y="100" fill="#F4F5F8"/>
      <rect width="900" height="200" y="200" fill="#2D2A4A"/>
    </svg>
  );
}

function LangSwitch({ lang, setLang }) {
  const mobile = typeof window !== "undefined" && window.innerWidth < 768;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:3, background:C.offwhite, borderRadius:8, border:`1px solid ${C.border}`, padding:3 }}>
      {[
        { code:"en", Flag:FlagUK, label:"EN" },
        { code:"th", Flag:FlagTH, label:"TH" },
      ].map(({ code, Flag, label }) => (
        <button
          key={code}
          onClick={() => setLang(code)}
          title={code === "en" ? "English" : "ภาษาไทย"}
          style={{
            display:"flex", alignItems:"center", gap: mobile ? 0 : 5,
            padding: mobile ? "5px 7px" : "4px 9px",
            border:"none", cursor:"pointer",
            borderRadius:6,
            background: lang===code ? C.navy : "transparent",
            color: lang===code ? C.white : C.slate,
            fontSize:10, fontWeight:700, letterSpacing:"0.06em",
            transition:"all 0.15s",
          }}
        >
          <Flag/>
          {!mobile && label}
        </button>
      ))}
    </div>
  );
}

function Badge({ s, t }) {
  const labels = { NORMAL: t.statusNormal, LOW: t.statusLow, HIGH: t.statusHigh, "—":"—" };
  const m = { NORMAL:{bg:C.greenL,c:C.green}, LOW:{bg:C.redL,c:C.red}, HIGH:{bg:C.redL,c:C.red} };
  const { bg, c } = m[s] || { bg:C.offwhite, c:C.slateL };
  return <span style={{ background:bg, color:c, fontSize:9, fontWeight:800, letterSpacing:"0.1em", padding:"2px 7px", borderRadius:4 }}>{labels[s]||s}</span>;
}

function Dot({ online, t }) {
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:12, color:online?C.green:C.red, fontWeight:600 }}>
      <span style={{ width:7, height:7, borderRadius:"50%", background:online?C.green:C.red, boxShadow:online?`0 0 7px ${C.green}`:"none", display:"inline-block" }}/>
      {online ? t.liveLabel : t.statusOffline}
    </span>
  );
}

function Pill({ label, active, color, onClick }) {
  return (
    <button onClick={onClick} style={{
      display:"inline-flex", alignItems:"center", gap:6, padding:"5px 12px", borderRadius:20,
      border:`1.5px solid ${active?color:C.border}`, background:active?color+"18":C.white,
      color:active?color:C.slateL, fontSize:12, fontWeight:600, cursor:"pointer", transition:"all 0.15s", whiteSpace:"nowrap",
    }}>
      <span style={{ width:8, height:8, borderRadius:"50%", background:active?color:C.border, flexShrink:0 }}/>
      {label}
    </button>
  );
}

function Spinner({ t }) {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:"48px", flexDirection:"column", gap:14 }}>
      <div style={{ width:36, height:36, border:`3px solid ${C.border}`, borderTop:`3px solid ${C.navy}`, borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>
      <div style={{ fontSize:13, color:C.slate }}>{t.connectingMsg}</div>
      <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
    </div>
  );
}

function ApiStatusBanner({ mode, t }) {
  if (mode==="live") return (
    <div style={{ background:C.greenL, border:`1px solid #BBF7D0`, borderRadius:8, padding:"8px 14px", marginBottom:16, fontSize:12, color:C.green, fontWeight:600, display:"flex", alignItems:"center", gap:8 }}>
      <span style={{ width:8, height:8, borderRadius:"50%", background:C.green, display:"inline-block", boxShadow:`0 0 6px ${C.green}` }}/>
      {t.liveConnected}
    </div>
  );
  return (
    <div style={{ background:C.amberL, border:`1px solid #FDE68A`, borderRadius:8, padding:"8px 14px", marginBottom:16, fontSize:12, color:"#92400E", fontWeight:600, display:"flex", alignItems:"center", gap:8 }}>
      <span>⚠️</span> {t.demoMode}
    </div>
  );
}

// ─── COMBINED CHART ───────────────────────────────────────────────────────────
function CombinedChart({ data, visibleKeys, normalize, thresholds, t, chlorineMode="clO2" }) {
  const norm = (v,key) => {
    if (!normalize) return v;
    const th = thresholds[key]||{}; const mn=th.min??0; const mx=th.max??100;
    return mx!==mn ? +((v-mn)/(mx-mn)).toFixed(4) : 0;
  };

  // Keys that have at least one real value — fall back to all visible if none yet
  const keysWithData = visibleKeys.filter(k =>
    data.some(pt => pt[k] !== undefined && pt[k] !== null && !isNaN(Number(pt[k])))
  );
  const renderKeys = keysWithData.length > 0 ? keysWithData : visibleKeys;

  const chartData = data.slice(-60).map(pt => {
    const row = { time: pt.time || "" };
    renderKeys.forEach(k => {
      const v = Number(pt[k]);
      if(!isNaN(v) && pt[k] !== undefined && pt[k] !== null) row[k] = norm(v, k);
    });
    return row;
  });

  // Debug: log what we have (remove after confirming works)
  // console.log("Chart keys with data:", keysWithData, "Points:", chartData.length);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active||!payload?.length) return null;
    return (
      <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:10, padding:"10px 14px", fontSize:11, boxShadow:"0 4px 16px rgba(0,0,0,0.1)", maxWidth:240 }}>
        <div style={{ fontWeight:700, color:C.text, marginBottom:6 }}>{label}</div>
        {payload.map(p => {
          const rawMeta=KEY_META[p.dataKey]; if(!rawMeta) return null;
          const m = p.dataKey === "clO2" ? { ...rawMeta, ...CHLORINE_DISPLAY_MODES[chlorineMode] } : rawMeta;
          const raw=data.find(d=>d.time===label)?.[p.dataKey];
          const s=statusOf(raw??0,p.dataKey);
          return (
            <div key={p.dataKey} style={{ display:"flex", alignItems:"center", gap:7, marginBottom:3 }}>
              <span style={{ width:8, height:8, borderRadius:"50%", background:m.color, display:"inline-block", flexShrink:0 }}/>
              <span style={{ color:C.textM }}>{m.short}:</span>
              <span style={{ fontWeight:700, color:statusColor(s) }}>{raw?.toFixed(3)}</span>
              <span style={{ color:C.slateL }}>{m.unit}</span>
              <Badge s={s} t={t}/>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={chartData} margin={{ top:5, right:16, left:normalize?-22:-10, bottom:5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={C.border}/>
        <XAxis dataKey="time" tick={{ fontSize:9, fill:C.slateL }} interval={9}/>
        <YAxis tick={{ fontSize:10, fill:C.slate }} tickFormatter={v=>normalize?`${(v*100).toFixed(0)}%`:v}/>
        <Tooltip content={<CustomTooltip/>}/>
        {!normalize && renderKeys.map(k=>[
          <ReferenceLine key={k+"min"} y={thresholds[k]?.min} stroke={KEY_META[k]?.color} strokeDasharray="3 2" strokeOpacity={0.3}/>,
          <ReferenceLine key={k+"max"} y={thresholds[k]?.max} stroke={KEY_META[k]?.color} strokeDasharray="3 2" strokeOpacity={0.3}/>,
        ])}
        {renderKeys.map(k=>(
          <Line key={k} type="monotone" dataKey={k} stroke={KEY_META[k]?.color||C.slate} strokeWidth={2.5} dot={false} isAnimationActive={false} connectNulls={true}/>
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── GATEWAY PANEL ────────────────────────────────────────────────────────────
function GatewayPanel({ gateway, sensors, history, visibleKeys, onToggleKey, normalize, setNormalize, thresholds, apiMode, t, isAdmin=false }) {
  const [collapsed, setCollapsed] = useState(false);
  const [chlorineMode, setChlorineModeState] = useState(() => getChlorineMode(gateway.id));
  const online = gateway.data?.iot_node_status==="online";
  const latest = history[history.length-1]||{};
  const hasChlorineSensor = sensors.some(s => SENSOR_NAME_MAP[s.name]?.key === "clO2");

  const applyChlorineMode = (mode) => {
    if (!isAdmin) return; // safety guard — only admins may change this
    setChlorineModeState(mode);
    setChlorineMode(gateway.id, mode);
  };

  const alerts = sensors.map(s=>{
    const raw=SENSOR_NAME_MAP[s.name]; if(!raw) return null;
    const m=displayMetaFor(raw, gateway.id, chlorineMode);
    const st=statusOf(s.sdata,m.key);
    if(st==="NORMAL"||!online) return null;
    return { key:m.key, label:t[m.labelKey]||m.key, val:s.sdata, unit:m.unit, status:st };
  }).filter(Boolean);

  return (
    <div style={{ background:C.white, borderRadius:14, border:`1px solid ${C.border}`, marginBottom:20, overflow:"hidden", boxShadow:"0 2px 8px rgba(0,0,0,0.04)" }}>
      <div onClick={()=>setCollapsed(o=>!o)} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 22px", borderBottom:`1px solid ${C.border}`, cursor:"pointer", background:collapsed?C.offwhite:C.white }}>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div style={{ width:40, height:40, borderRadius:10, background:"#EEF2FF", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>📡</div>
          <div>
            <div style={{ fontWeight:700, fontSize:14, color:C.text }}>{gateway.name}</div>
            <div style={{ fontSize:11, color:C.slate, marginTop:1 }}>
              {t.scene} {gateway.scene_name} · {t.idLabel} {gateway.id} · {t.codeLabel} {gateway.device_code}
            </div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          {alerts.length>0&&!collapsed&&<span style={{ background:C.redL, color:C.red, fontSize:11, fontWeight:700, padding:"3px 10px", borderRadius:20 }}>⚠️ {alerts.length}</span>}
          <Dot online={online} t={t}/>
          <span style={{ fontSize:18, color:C.slateL, transform:collapsed?"rotate(-90deg)":"rotate(0)", transition:"transform 0.2s" }}>⌄</span>
        </div>
      </div>

      {!collapsed&&(
        <div style={{ padding:"20px 22px" }}>
          {alerts.length>0&&(
            <div style={{ background:C.redL, border:`1px solid #FECACA`, borderRadius:9, padding:"10px 14px", marginBottom:18 }}>
              {alerts.map((a,i)=>(
                <div key={i} style={{ fontSize:12, fontWeight:600, color:C.red, display:"flex", alignItems:"center", gap:8, ...(i>0?{marginTop:5}:{}) }}>
                  ⚠️ {a.label}: <strong>{a.val?.toFixed(3)} {a.unit}</strong> — <Badge s={a.status} t={t}/>
                </div>
              ))}
            </div>
          )}
          {!online&&<div style={{ background:C.amberL, border:`1px solid #FDE68A`, borderRadius:9, padding:"10px 14px", marginBottom:18, fontSize:12, fontWeight:600, color:"#92400E" }}>⚠️ {t.offlineWarning}</div>}

          {/* Sensor tiles */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(155px,1fr))", gap:12, marginBottom:22 }}>
            {sensors.map(s=>{
              const raw=SENSOR_NAME_MAP[s.name]; if(!raw) return null;
              const m=displayMetaFor(raw, gateway.id, chlorineMode);
              const v=s.sdata??0; const st=statusOf(v,m.key); const active=visibleKeys.includes(m.key);
              return (
                <div key={s.id} onClick={()=>onToggleKey(m.key)} style={{
                  padding:"13px 15px", borderRadius:10, cursor:"pointer", transition:"all 0.15s",
                  border:`1.5px solid ${active?m.color+"66":C.border}`,
                  background:active?m.color+"08":C.offwhite, opacity:active?1:0.5,
                }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:7 }}>
                    <span style={{ fontSize:10, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:"0.05em" }}>{m.short}</span>
                    <span style={{ fontSize:13 }}>{m.icon}</span>
                  </div>
                  <div style={{ fontSize:28, fontWeight:800, color:active?C.text:C.slateL, letterSpacing:"-0.02em", fontFamily:"monospace" }}>{online?v.toFixed(2):"—"}</div>
                  <div style={{ fontSize:11, color:C.slateL, marginBottom:5 }}>{m.unit||"—"}</div>
                  <div style={{ marginBottom:4 }}>{online&&<Badge s={st} t={t}/>}</div>
                  <div style={{ fontSize:9, color:active?m.color:C.slateL, fontWeight:700 }}>{active?t.shownOnChart:t.hiddenTap}</div>
                  <div style={{ fontSize:10, fontWeight:600, color:active?m.color:C.slateL, marginTop:3 }}>{t[m.labelKey]}</div>
                </div>
              );
            })}
          </div>

          {hasChlorineSensor && isAdmin && (
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:18, padding:"8px 14px", background:C.offwhite, border:`1px solid ${C.border}`, borderRadius:9 }}>
              <span style={{ fontSize:12, fontWeight:600, color:C.text }}>{t.chlorineModeLabel}</span>
              <div style={{ display:"flex", gap:6 }}>
                <button onClick={()=>applyChlorineMode("clO2")} style={{ padding:"4px 12px", borderRadius:7, border:`1px solid ${chlorineMode==="clO2"?C.navy:C.border}`, background:chlorineMode==="clO2"?C.navy:C.white, color:chlorineMode==="clO2"?C.white:C.slate, fontSize:11, fontWeight:700, cursor:"pointer" }}>ClO₂</button>
                <button onClick={()=>applyChlorineMode("fcl")}  style={{ padding:"4px 12px", borderRadius:7, border:`1px solid ${chlorineMode==="fcl"?C.navy:C.border}`,  background:chlorineMode==="fcl"?C.navy:C.white,  color:chlorineMode==="fcl"?C.white:C.slate,  fontSize:11, fontWeight:700, cursor:"pointer" }}>FCL</button>
              </div>
            </div>
          )}

          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:10, marginBottom:12 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.text }}>
              {t.combinedChart}
              <span style={{ fontSize:11, fontWeight:400, color:C.slate, marginLeft:8 }}>{t.combinedChartSub}</span>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={()=>setNormalize(false)} style={{ padding:"5px 12px", borderRadius:7, border:`1px solid ${!normalize?C.navy:C.border}`, background:!normalize?C.navy:C.white, color:!normalize?C.white:C.slate, fontSize:11, fontWeight:600, cursor:"pointer" }}>{t.rawValues}</button>
              <button onClick={()=>setNormalize(true)}  style={{ padding:"5px 12px", borderRadius:7, border:`1px solid ${normalize?C.navy:C.border}`,  background:normalize?C.navy:C.white,  color:normalize?C.white:C.slate,  fontSize:11, fontWeight:600, cursor:"pointer" }}>{t.normalised}</button>
            </div>
          </div>

          <div style={{ display:"flex", flexWrap:"wrap", gap:7, marginBottom:14 }}>
            {sensors.map(s=>{ const raw=SENSOR_NAME_MAP[s.name]; if(!raw) return null; const m=displayMetaFor(raw, gateway.id, chlorineMode);
              return <Pill key={m.key} label={`${m.icon} ${t[m.labelKey]}`} active={visibleKeys.includes(m.key)} color={m.color} onClick={()=>onToggleKey(m.key)}/>;
            })}
          </div>

          {history.length > 1
            ? <CombinedChart data={history} visibleKeys={visibleKeys} normalize={normalize} thresholds={thresholds} t={t} chlorineMode={chlorineMode}/>
            : history.length === 1
              ? (
                <div style={{ padding:"32px", textAlign:"center", color:C.slate, fontSize:13 }}>
                  <div style={{ marginBottom:8 }}>⏳ Collecting data...</div>
                  <div style={{ fontSize:11, color:C.slateL }}>Graph will appear after a few seconds of readings</div>
                </div>
              )
              : <Spinner t={t}/>
          }
          <div style={{ marginTop:8, fontSize:10, color:C.slateL, textAlign:"right" }}>
            {apiMode==="live"?t.liveLabel:t.demoLabel} · {t.lastUpdate} {latest.time||"—"}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── HISTORY HELPERS ──────────────────────────────────────────────────────────
// NOTE: SENSOR_NAME_MAP maps several raw Hanic sensor "name" strings onto the
// same chart key (e.g. "余氯", "Chlorine", "Residual Chlorine" and "Chlorine
// Dioxide" all map to key "clO2"). If Hanic ever reports TWO physical/registered
// sensors that both resolve to the same key (a duplicate, a leftover/ghost
// sensor entry after a probe swap, a backup channel, etc.), whichever one is
// last in sensorArr silently overwrites the others for that timestamp — the
// tiles (which render every raw sensor separately) still look correct, but the
// combined chart only ever shows one of them, and if that one is frozen/stale
// the whole line stops moving even though the live tile keeps updating.
// This groups by key first, prefers a sensor that reports itself online, and
// warns loudly in the console so the actual duplicate/ghost sensor id can be
// identified and removed/disabled on the Hanic side.
function buildHistoryPoint(sensorArr) {
  const pt = { time: nowStr() };
  const byKey = {};

  sensorArr.forEach(s => {
    const m = SENSOR_NAME_MAP[s.name];
    if(!m) return;
    const raw = s.sdata ?? s.value ?? s.data;
    if(raw === undefined || raw === null || raw === "") return;
    const val = parseFloat(raw);
    if(isNaN(val)) return;
    (byKey[m.key] = byKey[m.key] || []).push({ ...s, _val: val });
  });

  Object.entries(byKey).forEach(([key, entries]) => {
    if(entries.length > 1) {
      console.warn(
        `[CHART COLLISION] ${entries.length} sensors map to chart key "${key}" — ` +
        `only one can be charted, values may conflict:`,
        entries.map(e => ({ id:e.id, name:e.name, alias:e.alias, value:e._val, status:e.data?.iot_sensor_status }))
      );
      const online = entries.filter(e => {
        const st = e.data?.iot_sensor_status;
        return st === 1 || st === "1" || String(st).toLowerCase() === "online";
      });
      const chosen = online.length > 0 ? online[online.length - 1] : entries[entries.length - 1];
      pt[key] = chosen._val;
    } else {
      pt[key] = entries[0]._val;
    }
  });

  return pt;
}
function buildInitHistory(sensorArr) {
  // Used only in demo mode — uses realistic random values
  const now=Date.now();
  return Array.from({length:60},(_,i)=>{
    const pt={ time:tsToTime(now-(59-i)*5000) };
    sensorArr.forEach(s=>{ const m=SENSOR_NAME_MAP[s.name]; if(m) pt[m.key]=rnd(m.threshMin+(m.threshMax-m.threshMin)*0.45,(m.threshMax-m.threshMin)*0.08); });
    return pt;
  });
}

// ─── MAIN DASHBOARD ───────────────────────────────────────────────────────────
// ─── MY DEVICES VIEW ──────────────────────────────────────────────────────────
function MyDevicesView({ user, onLogout, t }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxDevices, setMaxDevices] = useState(user.maxDevices || 3);

  const load = async () => {
    setLoading(true);
    try {
      const d = await apiCall("/auth/sessions");
      setSessions(d.sessions || []);
      setMaxDevices(d.maxDevices || 3);
    } catch(e) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const kickSession = async (id, deviceName) => {
    if(!window.confirm(`${t.sessionSignOut}: "${deviceName}"?`)) return;
    try {
      await apiCall(`/auth/sessions/${id}`, "DELETE");
      setSessions(prev => prev.filter(s => s.id !== id));
    } catch(e) { alert(e.message); }
  };

  return (
    <div style={{ maxWidth:640 }}>
      <h2 style={{ margin:"0 0 4px", fontSize:18, fontWeight:700, color:C.text }}>{t.sessionsTitle}</h2>
      <p style={{ margin:"0 0 24px", color:C.slate, fontSize:12 }}>{t.sessionsSub}</p>

      {/* Device count indicator */}
      <div style={{ background:C.white, borderRadius:12, padding:"16px 20px", border:`1px solid ${C.border}`, marginBottom:20, display:"flex", alignItems:"center", gap:16 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:6 }}>{t.deviceLimit}</div>
          <div style={{ height:8, background:C.border, borderRadius:4, overflow:"hidden" }}>
            <div style={{ height:"100%", background: sessions.length >= maxDevices ? C.red : C.navy, width:`${Math.min(100,(sessions.length/maxDevices)*100)}%`, borderRadius:4, transition:"width 0.3s" }}/>
          </div>
          <div style={{ fontSize:11, color:C.slateL, marginTop:4 }}>{sessions.length} / {maxDevices} {t.maxDevices}</div>
        </div>
        <button onClick={load} style={{ padding:"6px 12px", borderRadius:7, border:`1px solid ${C.border}`, background:C.white, color:C.slate, fontSize:11, cursor:"pointer" }}>↺</button>
      </div>

      {loading ? <Spinner t={t}/> : (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {sessions.length === 0 ? (
            <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:32, textAlign:"center", color:C.slateL }}>{t.noSessions}</div>
          ) : sessions.map((s, i) => (
            <div key={s.id} style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:"16px 18px", display:"flex", alignItems:"center", gap:14 }}>
              <div style={{ fontSize:28 }}>{s.device_type==="mobile"?"📱":s.device_type==="desktop"?"💻":"🌐"}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
                  <span style={{ fontSize:13, fontWeight:700, color:C.text }}>{s.device_name}</span>
                  {i === 0 && <span style={{ fontSize:9, fontWeight:800, background:C.greenL, color:C.green, padding:"2px 8px", borderRadius:20 }}>{t.currentSession}</span>}
                </div>
                <div style={{ fontSize:11, color:C.slateL }}>
                  {t.sessionLastSeen}: {new Date(s.last_seen).toLocaleString("en-GB")}
                </div>
                <div style={{ fontSize:10, color:C.slateL }}>IP: {s.ip_address}</div>
              </div>
              {i !== 0 && (
                <button onClick={()=>kickSession(s.id, s.device_name)} style={{ padding:"6px 12px", background:C.redL, color:C.red, border:"none", borderRadius:7, fontSize:11, fontWeight:700, cursor:"pointer", flexShrink:0 }}>
                  ✕ {t.sessionSignOut}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Big logout button */}
      <div style={{ marginTop:24, padding:20, background:C.white, borderRadius:12, border:`1px solid ${C.border}` }}>
        <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:4 }}>{t.logOut}</div>
        <div style={{ fontSize:12, color:C.slate, marginBottom:14 }}>Sign out from this device. You will need to log in again.</div>
        <button onClick={onLogout} style={{ padding:"11px 24px", background:C.red, color:C.white, border:"none", borderRadius:9, fontSize:13, fontWeight:700, cursor:"pointer" }}>
          ⏻ {t.logOut}
        </button>
      </div>
    </div>
  );
}


function Dashboard({ user, onLogout, lang, setLang }) {
  const t = T[lang];
  const [view,setView] = useState("dashboard");
  const [sideOpen,setSideOpen] = useState(false); // closed by default, especially on mobile
  const [thresholds,setThresholds] = useState(buildDefaultThresholds);
  const [devicePrefs,setDevicePrefs] = useState({});
  const [normalize,setNormalize] = useState(false);
  const [apiMode,setApiMode] = useState("loading");
  const [scenes,setScenes] = useState([]);
  const [gateways,setGateways] = useState([]);
  const [sensors,setSensors] = useState({});
  const [histories,setHistories] = useState({});

  // ── Bootstrap: load all live data from backend ──────────────────────────────
  useEffect(()=>{
    let cancelled=false;
    (async()=>{
      try {
        // Ensure token is set before any API calls
        const storedToken = localStorage.getItem("mlx_token");
        if(!window._midlannexToken && storedToken) {
          window._midlannexToken = storedToken;
        }
        if(!window._midlannexToken) {
          // No token yet — wait and retry
          setApiMode("loading");
          return;
        }

        // Step 1: Get all scenes from backend (which fetches from Hanic server-side)
        const scenesData = await HanicAPI.getScenes();
        if(cancelled) return;

        if(!scenesData || scenesData.length === 0) {
          // Backend unreachable or no scenes — fall back to demo
          setApiMode("demo");
          setScenes(MOCK_SCENES);
          setGateways(MOCK_GATEWAYS);
          setSensors({[MOCK_GATEWAYS[0].id]: MOCK_SENSORS});
          setHistories({[MOCK_GATEWAYS[0].id]: buildInitHistory(MOCK_SENSORS)});
          return;
        }

        setApiMode("live");
        setScenes(scenesData);

        // Step 2: Get gateways for each scene
        const gwAll = [];
        for(const scene of scenesData) {
          const gws = await HanicAPI.getGateways(scene.id);
          gwAll.push(...gws);
        }
        if(cancelled) return;
        setGateways(gwAll);

        // Step 3: Get live sensor data for each gateway
        for(const gw of gwAll) {
          const sensors = await HanicAPI.getSensors(gw.id, null);
          if(cancelled) return;
          setSensors(prev => ({...prev, [gw.id]: sensors}));
          // Pre-fill 60 history points using current live values so graph draws immediately
          if(sensors.length > 0) {
            const now = Date.now();
            // Pre-fill with exact current value — no fake variation
            // Real variation will appear naturally as polls come in
            const preHistory = Array.from({length: 60}, (_, i) => {
              const pt = { time: tsToTime(now - (59 - i) * 5000) };
              sensors.forEach(s => {
                const m = SENSOR_NAME_MAP[s.name];
                if(m) {
                  const val = parseFloat(s.sdata);
                  if(!isNaN(val)) pt[m.key] = val;
                }
              });
              return pt;
            });
            setHistories(prev => ({...prev, [gw.id]: preHistory}));
          }
        }
      } catch(err) {
        // Network error — fall back to demo mode
        console.warn("Live API failed, switching to demo mode:", err.message);
        setApiMode("demo");
        setScenes(MOCK_SCENES);
        setGateways(MOCK_GATEWAYS);
        setSensors({[MOCK_GATEWAYS[0].id]: MOCK_SENSORS});
        setHistories({[MOCK_GATEWAYS[0].id]: buildInitHistory(MOCK_SENSORS)});
      }
    })();
    return ()=>{ cancelled=true; };
  },[user?.id]); // re-run when user changes (after login/restore)

  // ── Polling: refresh sensor values every 10 seconds ─────────────────────────
  useEffect(()=>{
    if(apiMode === "loading") return;

    const id = setInterval(async()=>{
      if(apiMode === "demo") {
        // Demo: simulate sensor changes
        setSensors(prev=>{
          const n = {...prev};
          Object.keys(n).forEach(gwId => { n[gwId] = mockTick(n[gwId]); });
          return n;
        });
        setSensors(prev=>{
          Object.keys(prev).forEach(gwId => {
            const pt = buildHistoryPoint(prev[gwId]);
            setHistories(h => ({...h, [gwId]: [...(h[gwId]||[]).slice(-119), pt]}));
          });
          return prev;
        });
        return;
      }

      // Live: fetch fresh sensor data for each gateway
      for(const gw of gateways) {
        try {
          const freshSensors = await HanicAPI.getSensors(gw.id, null);
          if(freshSensors.length > 0) {
            setSensors(prev => ({...prev, [gw.id]: freshSensors}));
            const pt = buildHistoryPoint(freshSensors);
            setHistories(prev => ({...prev, [gw.id]: [...(prev[gw.id]||[]).slice(-119), pt]}));
            // Auto-update visible keys if they haven't been set yet
            setDevicePrefs(prev => {
              const cur = prev[gw.id];
              if(!cur || cur.visibleKeys.length === Object.keys(KEY_META).length) {
                const detectedKeys = freshSensors
                  .map(s => SENSOR_NAME_MAP[s.name]?.key)
                  .filter(Boolean);
                if(detectedKeys.length > 0 && detectedKeys.length < Object.keys(KEY_META).length) {
                  return {...prev, [gw.id]: { ...cur, visibleKeys: detectedKeys }};
                }
              }
              return prev;
            });
          }
        } catch(err) {
          console.warn(`Failed to poll gateway ${gw.id}:`, err.message);
        }
      }
    }, 3000); // poll every 3 seconds for responsive live reading

    return () => clearInterval(id);
  },[apiMode, gateways]);

  useEffect(()=>{
    if(!gateways.length) return;
    setDevicePrefs(prev=>{
      const n={...prev};
      gateways.forEach(gw=>{
        if(!n[gw.id]) {
          // Auto-detect which keys exist for this gateway from actual sensor data
          const gwSensors = sensors[gw.id] || [];
          const detectedKeys = gwSensors
            .map(s => SENSOR_NAME_MAP[s.name]?.key)
            .filter(Boolean);
          // Use detected keys if available, otherwise fall back to all keys
          n[gw.id] = { visibleKeys: detectedKeys.length > 0 ? detectedKeys : Object.keys(KEY_META) };
        }
      });
      return n;
    });
  },[gateways, sensors]);

  const toggleKey=(gwId,key)=>setDevicePrefs(prev=>{ const cur=prev[gwId]?.visibleKeys||Object.keys(KEY_META); const n=cur.includes(key)?(cur.length>1?cur.filter(x=>x!==key):cur):[...cur,key]; return {...prev,[gwId]:{...prev[gwId],visibleKeys:n}}; });

  const totalAlerts=gateways.reduce((acc,gw)=>{
    if(gw.data?.iot_node_status!=="online") return acc+1;
    return acc+(sensors[gw.id]||[]).filter(s=>{ const m=SENSOR_NAME_MAP[s.name]; return m&&statusOf(s.sdata,m.key)!=="NORMAL"; }).length;
  },0);

  const navItems=[
    { id:"dashboard", icon:"◉", label:t.navDashboard },
    { id:"history",   icon:"📈", label:t.navHistory },
    { id:"alarms",    icon:"🔔", label:t.navAlarms },
    { id:"reports",   icon:"📄", label:t.navReports },
    { id:"settings",  icon:"⚙️",  label:t.navSettings },
    { id:"mydevices", icon:"📱", label:t.sessionsTitle },
    ...(user.role==="admin"?[{ id:"admin", icon:"🛡",  label:t.navAdmin }]:[]),
  ];

  // Detect mobile (window width < 768px)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setSideOpen(false); // auto-close on mobile
    };
    window.addEventListener("resize", handler);
    // Also ensure closed on mount if mobile
    if (window.innerWidth < 768) setSideOpen(false);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // On mobile, sidebar is an overlay (closed by default)
  // On desktop, sidebar is persistent on the left
  const showOverlay = isMobile && sideOpen;

  return (
    <div style={{ display:"flex", minHeight:"100vh", fontFamily:"'Inter',-apple-system,sans-serif", background:C.offwhite, fontSize:14 }}>

      {/* ── MOBILE OVERLAY BACKDROP ────────────────────────────────────────── */}
      {showOverlay && (
        <div
          onClick={()=>setSideOpen(false)}
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:40 }}
        />
      )}

      {/* ── SIDEBAR ────────────────────────────────────────────────────────── */}
      {/* Desktop: always visible, collapsible to icons */}
      {/* Mobile: hidden by default, slides in as overlay */}
      <aside style={{
        width: 240,
        background: C.navyD,
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        // Desktop: static in layout
        // Mobile: fixed overlay that slides in/out
        ...(isMobile ? {
          position: "fixed",
          top: 0, left: 0, bottom: 0,
          zIndex: 50,
          transform: sideOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.25s ease",
          boxShadow: sideOpen ? "4px 0 24px rgba(0,0,0,0.3)" : "none",
        } : {
          position: "relative",
          width: sideOpen ? 240 : 62,
          transition: "width 0.2s",
        }),
      }}>
        {/* Brand */}
        <div style={{ padding:"22px 18px 18px", borderBottom:"1px solid rgba(255,255,255,0.08)", display:"flex", alignItems:"center", gap:10 }}>
          <Logo size={30}/>
          {(sideOpen || isMobile) && (
            <div>
              <div style={{ fontSize:12, fontWeight:800, color:C.white, letterSpacing:"0.02em" }}>MIDLANNEX</div>
              <div style={{ fontSize:8, color:"rgba(255,255,255,0.4)", letterSpacing:"0.1em" }}>WATER MONITORING</div>
            </div>
          )}
          {/* Close button on mobile */}
          {isMobile && (
            <button onClick={()=>setSideOpen(false)} style={{ marginLeft:"auto", background:"none", border:"none", color:"rgba(255,255,255,0.5)", fontSize:20, cursor:"pointer", padding:0 }}>✕</button>
          )}
        </div>

        {/* Nav items */}
        <nav style={{ flex:1, padding:"10px 0" }}>
          {navItems.map(n=>(
            <button key={n.id} onClick={()=>{ setView(n.id); if(isMobile) setSideOpen(false); }} style={{
              display:"flex", alignItems:"center", gap:11, width:"100%",
              padding:"12px 18px",
              background:view===n.id?"rgba(255,255,255,0.1)":"none",
              border:"none", cursor:"pointer",
              color:view===n.id?C.white:"rgba(255,255,255,0.55)",
              fontSize:13, fontWeight:view===n.id?700:400, textAlign:"left",
              borderLeft:view===n.id?`3px solid ${C.white}`:"3px solid transparent",
              transition:"all 0.15s",
            }}>
              <span style={{ fontSize:16, flexShrink:0 }}>{n.icon}</span>
              {(sideOpen || isMobile) && <span>{n.label}</span>}
            </button>
          ))}
        </nav>

        {/* Gateway status */}
        {(sideOpen || isMobile) && (
          <div style={{ padding:"12px 18px", borderTop:"1px solid rgba(255,255,255,0.08)" }}>
            <div style={{ fontSize:9, color:"rgba(255,255,255,0.35)", letterSpacing:"0.1em", marginBottom:8 }}>{t.gateways} ({gateways.length})</div>
            {gateways.map(gw=>(
              <div key={gw.id} style={{ display:"flex", alignItems:"center", gap:7, marginBottom:5 }}>
                <span style={{ width:6, height:6, borderRadius:"50%", background:gw.data?.iot_node_status==="online"?C.green:C.red, flexShrink:0 }}/>
                <span style={{ fontSize:10, color:"rgba(255,255,255,0.55)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{gw.name}</span>
              </div>
            ))}
          </div>
        )}

        {/* User info + logout */}
        <div style={{ padding:"12px 18px", borderTop:"1px solid rgba(255,255,255,0.08)" }}>
          {(sideOpen || isMobile) ? (
            <>
              <div style={{ fontSize:11, fontWeight:600, color:C.white }}>{user.name}</div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", marginBottom:10 }}>{user.company}</div>
              <button onClick={onLogout} style={{
                display:"flex", alignItems:"center", gap:6, width:"100%",
                padding:"8px 12px", background:"rgba(239,68,68,0.15)",
                border:"1px solid rgba(239,68,68,0.3)", borderRadius:8,
                color:"#FCA5A5", fontSize:12, fontWeight:700, cursor:"pointer",
              }}>
                <span>⏻</span> {t.logOut}
              </button>
            </>
          ) : (
            <button onClick={onLogout} title={t.logOut} style={{
              width:38, height:38, borderRadius:8, background:"rgba(239,68,68,0.15)",
              border:"1px solid rgba(239,68,68,0.3)", cursor:"pointer",
              color:"#FCA5A5", fontSize:16, display:"flex", alignItems:"center", justifyContent:"center",
            }}>⏻</button>
          )}
        </div>
      </aside>

      {/* ── MAIN CONTENT ───────────────────────────────────────────────────── */}
      <main style={{ flex:1, overflow:"auto", minWidth:0, paddingBottom: isMobile ? 70 : 0 }}>

        {/* TOPBAR */}
        <div style={{
          background:C.white, borderBottom:`1px solid ${C.border}`,
          padding:"0 14px", height:54,
          display:"flex", alignItems:"center", justifyContent:"space-between",
          position:"sticky", top:0, zIndex:20, gap:8,
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0 }}>
            {/* Hamburger — always visible on mobile, toggle on desktop */}
            <button onClick={()=>setSideOpen(o=>!o)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:18, color:C.slate, flexShrink:0, padding:"4px 6px" }}>☰</button>
            <div style={{ display:"flex", alignItems:"center", gap:8, minWidth:0 }}>
              <Logo size={22}/>
              <span style={{ fontWeight:700, color:C.text, fontSize:13, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                {navItems.find(n=>n.id===view)?.label}
              </span>
              {!isMobile && <span style={{ fontSize:9, color:C.slateL, background:C.offwhite, border:`1px solid ${C.border}`, borderRadius:4, padding:"1px 6px", fontWeight:600, letterSpacing:"0.06em", flexShrink:0 }}>{t.version}</span>}
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
            {totalAlerts>0&&(
              <div style={{ background:C.redL, border:`1px solid #FECACA`, borderRadius:20, padding:"3px 8px", fontSize:10, fontWeight:700, color:C.red, whiteSpace:"nowrap" }}>
                ⚠️ {totalAlerts}
              </div>
            )}
            <LangSwitch lang={lang} setLang={setLang}/>
            <a href="https://lin.ee/pxYlIH3" target="_blank" rel="noreferrer" style={{ padding:"6px 10px", background:"#06C755", color:C.white, borderRadius:7, fontSize:11, fontWeight:700, textDecoration:"none" }}>💬</a>
          </div>
        </div>

        {/* PAGE CONTENT */}
        <div style={{ padding: isMobile ? "16px 14px" : "24px 26px" }}>
          {apiMode!=="loading"&&<ApiStatusBanner mode={apiMode} t={t}/>}
          {view==="dashboard"&&(
            <div>
              <div style={{ marginBottom:16 }}>
                <h2 style={{ margin:"0 0 3px", fontSize:isMobile?16:18, fontWeight:700, color:C.text }}>{t.liveDashboard}</h2>
                <p style={{ margin:0, fontSize:12, color:C.slate }}>{gateways.length} {gateways.length===1?t.liveDashboardSub:t.gatewaysSub} · {t.hanicPlatform}</p>
              </div>
              {apiMode==="loading"&&<Spinner t={t}/>}
              {apiMode!=="loading"&&gateways.map(gw=>{
                // Detect which keys actually exist for this gateway
                const gwSensors = sensors[gw.id]||[];
                const detectedKeys = gwSensors
                  .map(s => SENSOR_NAME_MAP[s.name]?.key)
                  .filter(Boolean);
                // Use saved prefs if they exist and are narrower than all keys
                // Otherwise use detected keys from actual sensors
                const savedPrefs = devicePrefs[gw.id]?.visibleKeys;
                const effectiveKeys = savedPrefs && savedPrefs.length < Object.keys(KEY_META).length
                  ? savedPrefs
                  : detectedKeys.length > 0 ? detectedKeys : Object.keys(KEY_META);
                return (
                  <GatewayPanel key={gw.id} gateway={gw} sensors={gwSensors} history={histories[gw.id]||[]}
                    visibleKeys={effectiveKeys}
                    onToggleKey={k=>toggleKey(gw.id,k)} normalize={normalize} setNormalize={setNormalize}
                    thresholds={thresholds} apiMode={apiMode} t={t} isAdmin={user?.role==="admin"}/>
                );
              })}
            </div>
          )}
          {view==="history"  && <HistoryView  gateways={gateways} sensors={sensors} apiMode={apiMode} t={t} lang={lang} userEmail={user?.email}/>}
          {view==="alarms"   && <AlarmsView   apiMode={apiMode} t={t}/>}
          {view==="settings" && <SettingsView thresholds={thresholds} setThresholds={setThresholds} t={t}/>}
          {view==="reports"  && <ReportsView  user={user} t={t} lang={lang}/>}
          {view==="mydevices" && <MyDevicesView user={user} onLogout={onLogout} t={t}/>}
          {view==="admin" && user.role==="admin" && <AdminView gateways={gateways} scenes={scenes} t={t}/>}
        </div>

        {/* ── PAGE FOOTER ───────────────────────────────────────────────────── */}
        <div style={{
          borderTop:`1px solid ${C.border}`, background:C.white,
          padding:"16px 24px",
          display:"flex", alignItems:"center", justifyContent:"space-between",
          flexWrap:"wrap", gap:10,
          marginTop:8,
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
            <Logo size={18}/>
            <span style={{ fontSize:11, color:C.slate, fontWeight:600 }}>Midlannex Co., Ltd.</span>
            <span style={{ fontSize:10, color:C.slateL }}>· {t.version}</span>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
            <a href="https://midlannex.co.th" target="_blank" rel="noreferrer" style={{ fontSize:11, color:C.slate, textDecoration:"none", display:"flex", alignItems:"center", gap:4 }}>🌐 {t.website}</a>
            <a href="mailto:report@midlannex.co.th" style={{ fontSize:11, color:C.slate, textDecoration:"none", display:"flex", alignItems:"center", gap:4 }}>✉️ {t.contactEmail}</a>
            <a href="https://lin.ee/pxYlIH3" target="_blank" rel="noreferrer" style={{
              display:"flex", alignItems:"center", gap:5, padding:"5px 12px",
              background:"#06C755", color:C.white, borderRadius:7,
              fontSize:11, fontWeight:700, textDecoration:"none",
            }}>💬 {t.footerContact}</a>
          </div>
          <div style={{ fontSize:10, color:C.slateL, width:"100%", textAlign:"center" }}>
            {t.copyright} · {t.footerPrivacy}
          </div>
        </div>
      </main>

      {/* ── MOBILE BOTTOM NAV BAR ──────────────────────────────────────────── */}
      {isMobile && (
        <div style={{
          position:"fixed", bottom:0, left:0, right:0, zIndex:30,
          background:C.navyD, borderTop:"1px solid rgba(255,255,255,0.1)",
          display:"flex", height:62, paddingBottom:"env(safe-area-inset-bottom)",
        }}>
          {navItems.slice(0,5).map(n=>(
            <button key={n.id} onClick={()=>{ setView(n.id); setSideOpen(false); }} style={{
              flex:1, display:"flex", flexDirection:"column", alignItems:"center",
              justifyContent:"center", gap:3, border:"none", cursor:"pointer",
              background: view===n.id ? "rgba(255,255,255,0.12)" : "transparent",
              borderTop: view===n.id ? `2px solid ${C.white}` : "2px solid transparent",
              color: view===n.id ? C.white : "rgba(255,255,255,0.45)",
              padding:"6px 2px",
            }}>
              <span style={{ fontSize:18 }}>{n.icon}</span>
              <span style={{ fontSize:9, fontWeight:600, letterSpacing:"0.03em", whiteSpace:"nowrap" }}>
                {n.label.split(" ")[0]}
              </span>
            </button>
          ))}
        </div>
      )}

    </div>
  );
}

// ─── HISTORY VIEW ─────────────────────────────────────────────────────────────
function HistoryView({ gateways, sensors, apiMode, t, lang="en", userEmail="" }) {
  const [selGw,setSelGw]=useState(gateways[0]?.id||null);
  const [range,setRange]=useState("1h");
  const [iv,setIv]=useState("1m");
  const [pickedDate,setPickedDate]=useState(""); // "" = use quick range buttons; "YYYY-MM-DD" = a specific calendar day
  const [normalize,setNormalize]=useState(false);
  const [histData,setHistData]=useState([]);
  const [statusEvents,setStatusEvents]=useState([]);
  const [loading,setLoading]=useState(false);
  const [showReportModal,setShowReportModal]=useState(false);
  const [reportFrom,setReportFrom]=useState("");
  const [reportTo,setReportTo]=useState("");
  const [reportEmail,setReportEmail]=useState(userEmail||"");
  const [reportState,setReportState]=useState(null); // null | "sending" | "sent" | error string

  useEffect(()=>{ if(userEmail && !reportEmail) setReportEmail(userEmail); },[userEmail]);

  const submitReportRequest = async () => {
    if(!reportFrom || !reportTo || new Date(reportFrom) > new Date(reportTo)) {
      setReportState(t.reportRangeError);
      return;
    }
    setReportState("sending");
    try {
      await apiCall("/reports/trigger", "POST", {
        fromDate: `${reportFrom}T00:00:00`,
        toDate:   `${reportTo}T23:59:59`,
        email:    reportEmail || userEmail,
      });
      setReportState("sent");
    } catch(err) {
      setReportState(err.message || "Failed to request report.");
    }
  };

  useEffect(()=>{ if(gateways.length) setSelGw(gateways[0].id); },[gateways]);

  // Auto-detect visible keys from actual sensors for selected gateway
  const ss = sensors[selGw]||[];
  const detectedKeys = ss.map(s=>SENSOR_NAME_MAP[s.name]?.key).filter(Boolean);
  const [visibleKeys,setVisibleKeys]=useState(detectedKeys.length>0 ? detectedKeys : Object.keys(KEY_META));

  // Update visibleKeys when the SELECTED GATEWAY changes (not on every background
  // sensors refresh — `sensors` updates every few seconds from the live poller
  // even while viewing History, and previously this effect fired on every one
  // of those updates, silently re-checking any box the user had just unchecked).
  const initializedGwRef = useRef(null);
  useEffect(()=>{
    const keys = (sensors[selGw]||[]).map(s=>SENSOR_NAME_MAP[s.name]?.key).filter(Boolean);
    if(keys.length>0 && initializedGwRef.current !== selGw) {
      setVisibleKeys(keys);
      initializedGwRef.current = selGw;
    }
  },[selGw, sensors]);

  const loadHistory=useCallback(async()=>{
    if(!selGw) return;
    setLoading(true);
    const gwSensors = sensors[selGw]||[];

    if(apiMode==="demo") {
      setHistData(buildInitHistory(gwSensors.length > 0 ? gwSensors : MOCK_SENSORS));
      setStatusEvents([]);
      setLoading(false);
      return;
    }

    // Build the query window: either a specific calendar day (local midnight to
    // local 23:59:59, converted to UTC for the backend) or a quick relative range.
    let query;
    if (pickedDate) {
      const dayStart = new Date(`${pickedDate}T00:00:00`);
      const dayEnd   = new Date(`${pickedDate}T23:59:59`);
      const startTime = dayStart.toISOString().replace("T"," ").slice(0,19);
      const endTime   = dayEnd.toISOString().replace("T"," ").slice(0,19);
      query = `startTime=${encodeURIComponent(startTime)}&endTime=${encodeURIComponent(endTime)}`;
    } else {
      const hoursMap = { "1h":1, "6h":6, "24h":24, "7d":168 };
      query = `hours=${hoursMap[range] || 1}`;
    }
    // iv controls aggregation: "1m"/"1h" ask the backend to average into buckets;
    // "1s" asks for raw, ungrouped points (our own poll interval, not literally 1-second).
    if (iv === "1m" || iv === "1h") query += `&interval=${iv}`;

    try {
      const d = await apiCall(`/sensors/readings?gatewayId=${selGw}&${query}`);
      const rows = d.readings || [];
      setStatusEvents(d.statusEvents || []);
      console.log(`[HISTORY] Got ${rows.length} readings from local DB`);

      if(rows.length === 0) {
        setHistData([]);
        setLoading(false);
        return;
      }

      // Merge all sensor rows sharing the same recorded_at bucket into one
      // time-keyed object. (When the backend aggregates into 1m/1h buckets,
      // rows for different sensors in the same bucket share an identical
      // recorded_at string, so grouping directly on it works for both raw
      // and aggregated modes.)
      const merged = {};
      rows.forEach(row => {
        const m = SENSOR_NAME_MAP[row.sensor_name];
        if(!m) return;
        const ms = Date.parse(row.recorded_at.replace(" ","T")+"Z");
        if(isNaN(ms)) return;
        const dateStr = new Date(ms).toLocaleDateString("en-GB", {day:"2-digit", month:"2-digit", year: lang==="en" ? "numeric" : "2-digit"});
        const timeStr = new Date(ms).toLocaleTimeString("en-GB", {hour:"2-digit", minute:"2-digit"});
        if(!merged[row.recorded_at]) merged[row.recorded_at] = { time: `${dateStr} ${timeStr}`, _ms: ms };
        merged[row.recorded_at][m.key] = parseFloat(row.value);
      });

      const sorted = Object.values(merged)
        .sort((a,b) => a._ms - b._ms)
        .map(({_ms, ...rest}) => rest);

      console.log(`[HISTORY] Merged to ${sorted.length} time points`);
      setHistData(sorted);
    } catch(err) {
      console.error("loadHistory error:", err.message);
      setHistData([]);
      setStatusEvents([]);
    }
    setLoading(false);
  },[selGw,range,iv,pickedDate,apiMode,sensors,lang]);

  useEffect(()=>{ loadHistory(); },[loadHistory]);
  const toggleKey=k=>setVisibleKeys(prev=>prev.includes(k)?(prev.length>1?prev.filter(x=>x!==k):prev):[...prev,k]);

  // Turn the raw online/offline event list into readable "was offline from X to Y" periods
  const offlinePeriods = (() => {
    const periods = [];
    let openStart = null;
    statusEvents.forEach(e => {
      const ms = Date.parse(e.recorded_at.replace(" ","T")+"Z");
      if(isNaN(ms)) return;
      if(e.status === "offline" && openStart === null) openStart = ms;
      else if(e.status === "online" && openStart !== null) { periods.push({ start: openStart, end: ms }); openStart = null; }
    });
    if(openStart !== null) periods.push({ start: openStart, end: null });
    return periods;
  })();
  const fmtDT = ms => new Date(ms).toLocaleString("en-GB", {day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit"});
  const fmtDur = ms => {
    const mins = Math.round(ms/60000);
    if(mins < 60) return `${mins} min`;
    const h = Math.floor(mins/60), m = mins%60;
    return m ? `${h}h ${m}m` : `${h}h`;
  };

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:12, marginBottom:20 }}>
        <h2 style={{ margin:0, fontSize:18, fontWeight:700, color:C.text }}>{t.historyTitle}</h2>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
          <button onClick={()=>{ setShowReportModal(true); setReportState(null); }} style={{ padding:"6px 14px", borderRadius:8, border:`1px solid ${C.navy}`, background:C.navy, color:C.white, fontSize:11, fontWeight:700, cursor:"pointer" }}>{t.requestReportBtn}</button>
          {gateways.map(gw=>(
            <button key={gw.id} onClick={()=>setSelGw(gw.id)} style={{ padding:"6px 14px", borderRadius:8, border:`1px solid ${selGw===gw.id?C.navy:C.border}`, background:selGw===gw.id?C.navy:C.white, color:selGw===gw.id?C.white:C.textM, fontSize:11, fontWeight:600, cursor:"pointer" }}>
              <span style={{ width:6, height:6, borderRadius:"50%", background:gw.data?.iot_node_status==="online"?C.green:C.red, display:"inline-block", marginRight:6 }}/>
              {gw.name}
            </button>
          ))}
        </div>
      </div>
      <div style={{ background:C.white, borderRadius:12, padding:"16px 20px", border:`1px solid ${C.border}`, marginBottom:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:10, marginBottom:14 }}>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
            <span style={{ fontSize:11, color:C.slate, fontWeight:700 }}>{t.range}</span>
            {["1h","6h","24h","7d"].map(r=><button key={r} disabled={!!pickedDate} onClick={()=>setRange(r)} style={{ padding:"5px 12px", borderRadius:7, border:`1px solid ${!pickedDate&&range===r?C.navy:C.border}`, background:!pickedDate&&range===r?C.navy:C.white, color:!pickedDate&&range===r?C.white:C.slate, fontSize:11, fontWeight:600, cursor:pickedDate?"default":"pointer", opacity:pickedDate?0.4:1 }}>{r}</button>)}
            <span style={{ fontSize:11, color:C.slate, fontWeight:700, marginLeft:10 }}>{t.pickDay}</span>
            <input type="date" lang={lang==="en"?"en-GB":"th"} value={pickedDate} max={new Date().toISOString().slice(0,10)} onChange={e=>setPickedDate(e.target.value)}
              style={{ padding:"4px 10px", borderRadius:7, border:`1px solid ${pickedDate?C.navy:C.border}`, fontSize:11, fontWeight:600, color:C.text, cursor:"pointer" }}/>
            {pickedDate && <button onClick={()=>setPickedDate("")} style={{ padding:"5px 10px", borderRadius:7, border:`1px solid ${C.border}`, background:C.white, color:C.slate, fontSize:11, fontWeight:600, cursor:"pointer" }}>{t.clearDay}</button>}
            <span style={{ fontSize:11, color:C.slate, fontWeight:700, marginLeft:8 }}>{t.intervalLabel}</span>
            {["1s","1m","1h"].map(i2=><button key={i2} onClick={()=>setIv(i2)} style={{ padding:"5px 12px", borderRadius:7, border:`1px solid ${iv===i2?C.navy:C.border}`, background:iv===i2?C.navy:C.white, color:iv===i2?C.white:C.slate, fontSize:11, fontWeight:600, cursor:"pointer" }}>{i2}</button>)}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={()=>setNormalize(false)} style={{ padding:"5px 12px", borderRadius:7, border:`1px solid ${!normalize?C.navy:C.border}`, background:!normalize?C.navy:C.white, color:!normalize?C.white:C.slate, fontSize:11, fontWeight:600, cursor:"pointer" }}>{t.rawValues}</button>
            <button onClick={()=>setNormalize(true)}  style={{ padding:"5px 12px", borderRadius:7, border:`1px solid ${normalize?C.navy:C.border}`,  background:normalize?C.navy:C.white,  color:normalize?C.white:C.slate,  fontSize:11, fontWeight:600, cursor:"pointer" }}>{t.normalised}</button>
            <button onClick={loadHistory} style={{ padding:"5px 12px", borderRadius:7, border:`1px solid ${C.border}`, background:C.white, color:C.slate, fontSize:11, fontWeight:600, cursor:"pointer" }}>{t.refresh}</button>
          </div>
        </div>
        <div style={{ display:"flex", flexWrap:"wrap", gap:7 }}>
          {(sensors[selGw]||[]).map(s=>{ const raw=SENSOR_NAME_MAP[s.name]; if(!raw) return null; const m=displayMetaFor(raw, selGw, getChlorineMode(selGw)); return <Pill key={m.key} label={`${m.icon} ${t[m.labelKey]}`} active={visibleKeys.includes(m.key)} color={m.color} onClick={()=>toggleKey(m.key)}/>; })}
        </div>
      </div>

      {offlinePeriods.length > 0 && (
        <div style={{ background:C.redL, border:`1px solid ${C.red}33`, borderRadius:12, padding:"12px 16px", marginBottom:16 }}>
          <div style={{ fontSize:12, fontWeight:700, color:C.red, marginBottom:6 }}>{t.offlineBannerTitle}</div>
          {offlinePeriods.map((p,i)=>(
            <div key={i} style={{ fontSize:11, color:C.red, fontFamily:"monospace" }}>
              {p.end
                ? t.offlinePeriodRange.replace("{start}",fmtDT(p.start)).replace("{end}",fmtDT(p.end)).replace("{dur}",fmtDur(p.end-p.start))
                : t.offlinePeriodOngoing.replace("{time}",fmtDT(p.start))}
            </div>
          ))}
        </div>
      )}

      <div style={{ background:C.white, borderRadius:14, padding:"22px 22px 14px", border:`1px solid ${C.border}`, marginBottom:20 }}>
        {loading ? <Spinner t={t}/> : histData.length === 0 ? (
          <div style={{ padding:"48px", textAlign:"center", color:C.slate }}>
            <div style={{ fontSize:24, marginBottom:12 }}>📭</div>
            <div style={{ fontSize:14, fontWeight:600, color:C.text, marginBottom:6 }}>No data yet for this period</div>
            <div style={{ fontSize:12, color:C.slateL }}>Data is collected continuously in the background. Try a different day or a shorter range.</div>
          </div>
        ) : <CombinedChart data={histData} visibleKeys={visibleKeys} normalize={normalize} thresholds={buildDefaultThresholds()} t={t} chlorineMode={getChlorineMode(selGw)}/>}
      </div>
      <div style={{ background:C.white, borderRadius:14, padding:22, border:`1px solid ${C.border}` }}>
        <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:14 }}>{t.readingLog}</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead><tr style={{ background:C.offwhite }}>
              {[t.dateTimeCol,...visibleKeys.map(k=>`${KEY_META[k]?.short||k} (${KEY_META[k]?.unit||""})`)].map(h=>(
                <th key={h} style={{ padding:"9px 14px", textAlign:"left", fontWeight:700, color:C.slate, borderBottom:`2px solid ${C.border}`, whiteSpace:"nowrap", fontSize:10, letterSpacing:"0.05em", textTransform:"uppercase" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {[...histData].reverse().slice(0,30).map((row,i)=>(
                <tr key={i} style={{ borderBottom:`1px solid ${C.border}`, background:i%2===0?C.white:C.offwhite }}>
                  <td style={{ padding:"8px 14px", fontFamily:"monospace", color:C.slate, fontSize:11, whiteSpace:"nowrap" }}>{row.time}</td>
                  {visibleKeys.map(k=>{ const s=statusOf(row[k]??0,k); return <td key={k} style={{ padding:"8px 14px", fontWeight:600, color:statusColor(s) }}>{row[k]!=null?row[k].toFixed(3):"—"}</td>; })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showReportModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:20 }} onClick={()=>setShowReportModal(false)}>
          <div style={{ background:C.white, borderRadius:14, padding:24, width:"100%", maxWidth:420 }} onClick={e=>e.stopPropagation()}>
            <div style={{ fontSize:16, fontWeight:700, color:C.text, marginBottom:6 }}>{t.requestReportTitle}</div>
            <div style={{ fontSize:12, color:C.slate, marginBottom:18 }}>{t.requestReportDesc}</div>

            <div style={{ display:"flex", gap:10, marginBottom:12 }}>
              <div style={{ flex:1 }}>
                <label style={{ fontSize:11, fontWeight:700, color:C.slate, display:"block", marginBottom:4 }}>{t.reportFromLabel}</label>
                <input type="date" lang={lang==="en"?"en-GB":"th"} value={reportFrom} max={new Date().toISOString().slice(0,10)} onChange={e=>setReportFrom(e.target.value)}
                  style={{ width:"100%", padding:"8px 10px", borderRadius:8, border:`1px solid ${C.border}`, fontSize:12, boxSizing:"border-box" }}/>
              </div>
              <div style={{ flex:1 }}>
                <label style={{ fontSize:11, fontWeight:700, color:C.slate, display:"block", marginBottom:4 }}>{t.reportToLabel}</label>
                <input type="date" lang={lang==="en"?"en-GB":"th"} value={reportTo} max={new Date().toISOString().slice(0,10)} onChange={e=>setReportTo(e.target.value)}
                  style={{ width:"100%", padding:"8px 10px", borderRadius:8, border:`1px solid ${C.border}`, fontSize:12, boxSizing:"border-box" }}/>
              </div>
            </div>

            <label style={{ fontSize:11, fontWeight:700, color:C.slate, display:"block", marginBottom:4 }}>{t.reportEmailLabel}</label>
            <input type="email" value={reportEmail} onChange={e=>setReportEmail(e.target.value)} placeholder="you@example.com"
              style={{ width:"100%", padding:"8px 10px", borderRadius:8, border:`1px solid ${C.border}`, fontSize:12, boxSizing:"border-box", marginBottom:16 }}/>

            {reportState && reportState!=="sending" && reportState!=="sent" && (
              <div style={{ fontSize:12, color:C.red, marginBottom:12 }}>{reportState}</div>
            )}
            {reportState==="sent" && (
              <div style={{ fontSize:12, color:C.green, marginBottom:12 }}>{t.reportSent}</div>
            )}

            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={()=>setShowReportModal(false)} style={{ padding:"8px 16px", borderRadius:8, border:`1px solid ${C.border}`, background:C.white, color:C.slate, fontSize:12, fontWeight:600, cursor:"pointer" }}>{t.reportCancelBtn}</button>
              <button onClick={submitReportRequest} disabled={reportState==="sending"} style={{ padding:"8px 16px", borderRadius:8, border:`1px solid ${C.navy}`, background:C.navy, color:C.white, fontSize:12, fontWeight:700, cursor:reportState==="sending"?"default":"pointer", opacity:reportState==="sending"?0.6:1 }}>
                {reportState==="sending" ? t.reportSending : t.reportSendBtn}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ALARMS VIEW ──────────────────────────────────────────────────────────────
function AlarmsView({ apiMode, t }) {
  const [alarms,setAlarms]=useState([]);
  const [loading,setLoading]=useState(true);
  useEffect(()=>{
    (async()=>{
      if(apiMode==="demo"){ setAlarms([
        { id:1, sensor_name:"余氯", sdata:0.3, alarm_sdata:1.0, node_name:"智能水质监测系统平台", atimestr:"2026-06-13 10:30:00", iot_alarm_status:431, data:{ iot_alarm_status:"报警中", iot_trigger_alarm_level:"严重" } },
        { id:2, sensor_name:"PH",   sdata:9.2, alarm_sdata:8.5, node_name:"智能水质监测系统平台", atimestr:"2026-06-13 09:15:00", iot_alarm_status:432, data:{ iot_alarm_status:"恢复正常", iot_trigger_alarm_level:"一般" } },
      ]); setLoading(false); return; }
      const end=new Date().toISOString().replace("T"," ").slice(0,19);
      const start=new Date(Date.now()-86400000).toISOString().replace("T"," ").slice(0,19);
      const data=await HanicAPI.getAlarms({ start_time:start, end_time:end });
      setAlarms(data); setLoading(false);
    })();
  },[apiMode]);

  return (
    <div>
      <h2 style={{ margin:"0 0 4px", fontSize:18, fontWeight:700, color:C.text }}>{t.alarmsTitle}</h2>
      <p style={{ margin:"0 0 20px", color:C.slate, fontSize:12 }}>{t.alarmsSub}</p>
      {loading?<Spinner t={t}/>:(
        <div style={{ background:C.white, borderRadius:14, border:`1px solid ${C.border}`, overflow:"hidden" }}>
          {alarms.length===0
            ?<div style={{ padding:40, textAlign:"center", color:C.slateL, fontSize:13 }}>{t.noAlarms}</div>
            :<table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead><tr style={{ background:C.offwhite }}>{[t.alarmTime,t.alarmSensor,t.alarmGateway,t.alarmValue,t.alarmThreshold,t.alarmLevel,t.alarmStatus].map(h=><th key={h} style={{ padding:"10px 16px", textAlign:"left", fontWeight:700, color:C.slate, borderBottom:`2px solid ${C.border}`, fontSize:10, letterSpacing:"0.06em", textTransform:"uppercase", whiteSpace:"nowrap" }}>{h}</th>)}</tr></thead>
              <tbody>
                {alarms.map((a,i)=>{
                  const active=a.iot_alarm_status===431;
                  const lvlColor=a.data?.iot_trigger_alarm_level==="严重"?C.red:C.amber;
                  return(
                    <tr key={a.id||i} style={{ borderBottom:`1px solid ${C.border}`, background:i%2===0?C.white:C.offwhite }}>
                      <td style={{ padding:"11px 16px", fontFamily:"monospace", fontSize:11, color:C.slate }}>{a.atimestr}</td>
                      <td style={{ padding:"11px 16px", fontWeight:600, color:C.text }}>{a.sensor_name}</td>
                      <td style={{ padding:"11px 16px", color:C.textM }}>{a.node_name}</td>
                      <td style={{ padding:"11px 16px", fontWeight:700, color:C.red }}>{a.sdata}</td>
                      <td style={{ padding:"11px 16px", color:C.slate }}>{a.alarm_sdata}</td>
                      <td style={{ padding:"11px 16px" }}><span style={{ fontSize:10, fontWeight:700, color:lvlColor }}>{a.data?.iot_trigger_alarm_level||"—"}</span></td>
                      <td style={{ padding:"11px 16px" }}><span style={{ fontSize:10, fontWeight:700, padding:"2px 9px", borderRadius:20, background:active?C.redL:C.greenL, color:active?C.red:C.green }}>{active?t.alarmActive:t.alarmResolved}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          }
        </div>
      )}
    </div>
  );
}

// ─── SETTINGS VIEW ────────────────────────────────────────────────────────────
function SettingsView({ thresholds, setThresholds, t }) {
  const [local,setLocal]=useState(thresholds);
  const [saved,setSaved]=useState(false);
  const upd=(k,f,v)=>{ setLocal(p=>({...p,[k]:{...p[k],[f]:parseFloat(v)||0}})); setSaved(false); };
  const save=()=>{ setThresholds(local); setSaved(true); setTimeout(()=>setSaved(false),2500); };
  return (
    <div style={{ maxWidth:680 }}>
      <h2 style={{ margin:"0 0 4px", fontSize:18, fontWeight:700, color:C.text }}>{t.settingsTitle}</h2>
      <p style={{ margin:"0 0 24px", color:C.slate, fontSize:12 }}>{t.settingsSub}</p>
      {Object.entries(local).map(([key,cfg])=>{ const m=KEY_META[key]; if(!m) return null; return(
        <div key={key} style={{ background:C.white, borderRadius:12, padding:"18px 22px", border:`1px solid ${C.border}`, marginBottom:11 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:14 }}>
            <span style={{ width:10, height:10, borderRadius:"50%", background:m.color, display:"inline-block" }}/>
            <span style={{ fontWeight:700, fontSize:14, color:C.text }}>{t[m.labelKey]}</span>
            <span style={{ fontSize:11, color:m.color, fontWeight:600 }}>({m.unit||"—"})</span>
            {key==="clO2"&&<span style={{ marginLeft:"auto", fontSize:9, fontWeight:800, color:C.navy, background:"#EEF2FF", padding:"2px 9px", borderRadius:20 }}>{t.primaryLabel}</span>}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            <div><label style={LBL}>{t.minimum}</label><input type="number" step="0.1" value={cfg.min} onChange={e=>upd(key,"min",e.target.value)} style={INP}/></div>
            <div><label style={LBL}>{t.maximum}</label><input type="number" step="0.1" value={cfg.max} onChange={e=>upd(key,"max",e.target.value)} style={INP}/></div>
          </div>
        </div>
      );})}
      <button onClick={save} style={{ padding:"11px 28px", background:saved?C.green:C.navy, color:C.white, border:"none", borderRadius:10, fontSize:13, fontWeight:700, cursor:"pointer", transition:"background 0.3s", marginTop:4 }}>
        {saved?t.saved:t.saveChanges}
      </button>
    </div>
  );
}

// ─── REPORTS VIEW ─────────────────────────────────────────────────────────────
function ReportsView({ user, t, lang }) {
  const [email,setEmail]=useState(user.email);
  const [freq,setFreq]=useState("monthly");
  const [done,setDone]=useState(false);
  return (
    <div style={{ maxWidth:620 }}>
      <h2 style={{ margin:"0 0 4px", fontSize:18, fontWeight:700, color:C.text }}>{t.reportsTitle}</h2>
      <p style={{ margin:"0 0 24px", color:C.slate, fontSize:12 }}>{t.reportsSub}</p>
      <div style={{ background:C.white, borderRadius:14, padding:26, border:`1px solid ${C.border}`, marginBottom:16 }}>
        <label style={LBL}>{t.deliveryEmail}</label>
        <input value={email} onChange={e=>setEmail(e.target.value)} style={{...INP,marginBottom:14}} type="email"/>
        <label style={LBL}>{t.frequency}</label>
        <div style={{ display:"flex", gap:10, marginBottom:18 }}>
          {["weekly","monthly"].map(f=>(
            <button key={f} onClick={()=>setFreq(f)} style={{ flex:1, padding:10, borderRadius:9, border:`1px solid ${freq===f?C.navy:C.border}`, background:freq===f?C.navy:C.white, color:freq===f?C.white:C.textM, fontSize:12, fontWeight:600, cursor:"pointer" }}>
              {f==="weekly"?t.weekly:t.monthly}
            </button>
          ))}
        </div>
        <div style={{ background:C.offwhite, borderRadius:9, padding:"13px 15px", marginBottom:18 }}>
          <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:6 }}>{t.reportIncludes}</div>
          {t.reportItems.map((x,i)=>(
            <div key={i} style={{ fontSize:11, color:C.textM, padding:"2px 0", display:"flex", gap:7 }}><span style={{ color:C.green }}>✓</span>{x}</div>
          ))}
        </div>
        {done&&<div style={{ background:C.greenL, border:`1px solid #BBF7D0`, borderRadius:8, padding:"9px 13px", marginBottom:14, fontSize:12, color:C.green, fontWeight:700 }}>{t.reportActivated} {email}.</div>}
        <button onClick={()=>setDone(true)} style={{ padding:"11px 24px", background:C.navy, color:C.white, border:"none", borderRadius:9, fontSize:13, fontWeight:700, cursor:"pointer" }}>
          {freq==="weekly"?t.activateWeekly:t.activateMonthly}
        </button>
      </div>
    </div>
  );
}

// ─── ADMIN VIEW ───────────────────────────────────────────────────────────────
// ─── API HELPER ───────────────────────────────────────────────────────────────
async function apiCall(path, method="GET", body=null) {
  const headers = { "Content-Type":"application/json" };
  if (window._midlannexToken) headers["Authorization"] = `Bearer ${window._midlannexToken}`;
  const opts = { method, headers };
  if (body) opts.body = JSON.stringify(body);
  const r = await fetch(`${API_BASE}${path}`, opts);
  const d = await r.json();
  if (!r.ok) throw new Error(d.error || "Request failed");
  return d;
}

// ─── ADMIN SESSIONS VIEW ──────────────────────────────────────────────────────
function AdminSessionsView({ users, t }) {
  const [sessions, setSessions] = useState({});
  const [loading, setLoading] = useState(true);
  const [kicking, setKicking] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const result = {};
      for(const u of users) {
        try {
          const d = await apiCall(`/admin/users/${u.id}/sessions`);
          if(d.sessions?.length > 0) result[u.id] = { user: u, sessions: d.sessions };
        } catch(e) {}
      }
      setSessions(result);
      setLoading(false);
    })();
  }, [users]);

  const kickAll = async (userId) => {
    setKicking(userId);
    try {
      await apiCall(`/admin/users/${userId}/sessions`, "DELETE");
      setSessions(prev => { const n = {...prev}; delete n[userId]; return n; });
    } catch(e) { alert(e.message); }
    setKicking(null);
  };

  const kickOne = async (userId, sessionId, deviceName) => {
    if(!window.confirm(`Sign out "${deviceName}"?`)) return;
    try {
      await apiCall(`/auth/sessions/${sessionId}`, "DELETE");
      setSessions(prev => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          sessions: prev[userId].sessions.filter(s => s.id !== sessionId)
        }
      }));
    } catch(e) { alert(e.message); }
  };

  if(loading) return <Spinner t={t}/>;

  const allSessions = Object.values(sessions);
  const totalActive = allSessions.reduce((a, u) => a + u.sessions.length, 0);

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div style={{ fontSize:12, color:C.slate }}>
          {totalActive} active session{totalActive !== 1 ? "s" : ""} across {allSessions.length} account{allSessions.length !== 1 ? "s" : ""}
        </div>
      </div>

      {allSessions.length === 0 ? (
        <div style={{ background:C.white, borderRadius:14, border:`1px solid ${C.border}`, padding:40, textAlign:"center", color:C.slateL }}>
          {t.noSessions}
        </div>
      ) : allSessions.map(({ user: u, sessions: ss }) => (
        <div key={u.id} style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, marginBottom:14, overflow:"hidden" }}>
          <div style={{ background:C.offwhite, padding:"12px 18px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div>
              <span style={{ fontWeight:700, color:C.text, fontSize:13 }}>{u.name}</span>
              <span style={{ fontSize:11, color:C.slateL, marginLeft:8 }}>{u.email}</span>
              <span style={{ marginLeft:8, fontSize:10, fontWeight:700, background:"#EEF2FF", color:C.navy, padding:"2px 8px", borderRadius:20 }}>
                {ss.length}/{u.maxDevices||3} devices
              </span>
            </div>
            <button onClick={()=>kickAll(u.id)} disabled={kicking===u.id} style={{ padding:"5px 12px", background:C.redL, color:C.red, border:"none", borderRadius:7, fontSize:11, fontWeight:700, cursor:"pointer" }}>
              {kicking===u.id?"Signing out…":"Sign out all"}
            </button>
          </div>
          <div style={{ padding:"8px 0" }}>
            {ss.map((s, i) => (
              <div key={s.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 18px", borderBottom: i < ss.length-1 ? `1px solid ${C.border}` : "none" }}>
                <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                  <span style={{ fontSize:20 }}>{s.device_type==="mobile"?"📱":s.device_type==="desktop"?"💻":"🌐"}</span>
                  <div>
                    <div style={{ fontSize:12, fontWeight:600, color:C.text }}>{s.device_name}</div>
                    <div style={{ fontSize:10, color:C.slateL }}>
                      IP: {s.ip_address} · {t.sessionLastSeen}: {new Date(s.last_seen).toLocaleString("en-GB")}
                    </div>
                  </div>
                </div>
                <button onClick={()=>kickOne(u.id, s.id, s.device_name)} style={{ padding:"4px 10px", background:C.redL, color:C.red, border:"none", borderRadius:6, fontSize:10, fontWeight:700, cursor:"pointer" }}>
                  ✕ {t.sessionSignOut}
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


function AdminView({ gateways, scenes, t }) {
  const [users, setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [tab, setTab]       = useState("accounts");
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nu, setNu] = useState({ email:"", name:"", company:"", password:"", subscriptionExpiry:"", role:"user" });
  const [eu, setEu] = useState({});

  // ── Load users from backend ──────────────────────────────────────────────────
  const loadUsers = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await apiCall("/admin/users");
      // Normalise field names from backend snake_case → camelCase
      setUsers((d.users||[]).map(u => ({
        id:                 u.id,
        name:               u.name,
        email:              u.email,
        role:               u.role,
        company:            u.company,
        subscriptionStatus: u.subscriptionStatus || u.subscription_status,
        subscriptionExpiry: u.subscriptionExpiry || u.subscription_expiry || "",
        sceneIds:           u.sceneIds || u.scene_ids || [],
        maxDevices:         u.maxDevices || u.max_devices || 3,
      })));
    } catch(e) {
      setError(e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // ── Add user ─────────────────────────────────────────────────────────────────
  const addUser = async () => {
    if (!nu.email || !nu.name || !nu.password) return;
    setSaving(true);
    try {
      await apiCall("/admin/users", "POST", {
        name:               nu.name,
        email:              nu.email,
        password:           nu.password,
        company:            nu.company,
        role:               nu.role,
        subscriptionStatus: "active",
        subscriptionExpiry: nu.subscriptionExpiry === "permanent" ? "" : nu.subscriptionExpiry,
        maxDevices:         nu.maxDevices || 3,
      });
      setNu({ email:"", name:"", company:"", password:"", subscriptionExpiry:"", role:"user" });
      setShowAdd(false);
      await loadUsers();
    } catch(e) { alert(e.message); }
    setSaving(false);
  };

  // ── Edit user ─────────────────────────────────────────────────────────────────
  const startEdit = (u) => { setEditId(u.id); setEu({...u}); setShowAdd(false); };
  const cancelEdit = () => setEditId(null);

  const saveEdit = async () => {
    setSaving(true);
    try {
      await apiCall(`/admin/users/${editId}`, "PUT", {
        name:               eu.name,
        email:              eu.email,
        company:            eu.company,
        role:               eu.role,
        subscriptionStatus: eu.subscriptionStatus,
        subscriptionExpiry: eu.subscriptionExpiry === "permanent" ? "" : eu.subscriptionExpiry,
        maxDevices:         eu.maxDevices || 3,
      });
      setEditId(null);
      await loadUsers();
    } catch(e) { alert(e.message); }
    setSaving(false);
  };

  // ── Suspend / Restore ─────────────────────────────────────────────────────────
  const toggleSuspend = async (u) => {
    const action = u.subscriptionStatus === "suspended" ? "restore" : "suspend";
    try {
      await apiCall(`/admin/users/${u.id}/${action}`, "PUT");
      await loadUsers();
    } catch(e) { alert(e.message); }
  };

  // ── Remove user ───────────────────────────────────────────────────────────────
  const removeUser = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    try {
      await apiCall(`/admin/users/${id}`, "DELETE");
      if (editId === id) setEditId(null);
      await loadUsers();
    } catch(e) { alert(e.message); }
  };

  return (
    <div>
      <h2 style={{ margin:"0 0 4px", fontSize:18, fontWeight:700, color:C.text }}>{t.adminTitle}</h2>
      <p style={{ margin:"0 0 20px", color:C.slate, fontSize:12 }}>{t.adminSub}</p>
      <div style={{ display:"flex", gap:8, marginBottom:20, flexWrap:"wrap" }}>
        {[["accounts",t.accounts],["gateways",t.gatewaysTab],["scenes",t.scenesTab],["sessions",t.activeSessions]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{ padding:"7px 18px", borderRadius:8, border:`1px solid ${tab===id?C.navy:C.border}`, background:tab===id?C.navy:C.white, color:tab===id?C.white:C.textM, fontSize:12, fontWeight:600, cursor:"pointer" }}>{label}</button>
        ))}
      </div>

      {tab==="accounts"&&(
        <>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div style={{ fontSize:12, color:C.slate }}>
              {loading ? "Loading…" : `${users.length} ${t.accountsCount} · ${users.filter(u=>u.subscriptionStatus==="active").length} ${t.active}`}
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={loadUsers} style={{ padding:"7px 12px", background:C.offwhite, color:C.slate, border:`1px solid ${C.border}`, borderRadius:8, fontSize:12, cursor:"pointer" }}>↺ Refresh</button>
              <button onClick={()=>{ setShowAdd(o=>!o); setEditId(null); }} style={{ padding:"7px 16px", background:C.navy, color:C.white, border:"none", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer" }}>{t.addAccount}</button>
            </div>
          </div>

          {error && <div style={{ background:C.redL, border:`1px solid #FECACA`, borderRadius:8, padding:"10px 14px", marginBottom:14, fontSize:12, color:C.red, fontWeight:600 }}>⚠️ {error}</div>}

          {/* ADD ACCOUNT FORM */}
          {showAdd&&(
            <div style={{ background:C.white, borderRadius:12, padding:22, border:`1px solid ${C.border}`, marginBottom:16 }}>
              <div style={{ fontSize:14, fontWeight:700, marginBottom:14, color:C.text }}>{t.newAccount}</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                {[["name",t.fullName],["email","Email"],["company",t.company],["password",t.password]].map(([k,l])=>(
                  <div key={k}>
                    <label style={LBL}>{l}</label>
                    <input value={nu[k]} onChange={e=>setNu(p=>({...p,[k]:e.target.value}))} style={INP} type={k==="password"?"password":"text"}/>
                  </div>
                ))}
                <div style={{ gridColumn:"1/-1" }}>
                  <label style={LBL}>{t.expiry}</label>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <input value={nu.subscriptionExpiry==="permanent"?"":nu.subscriptionExpiry} onChange={e=>setNu(p=>({...p,subscriptionExpiry:e.target.value}))} placeholder="YYYY-MM-DD" disabled={nu.subscriptionExpiry==="permanent"} style={{...INP,flex:1,opacity:nu.subscriptionExpiry==="permanent"?0.4:1}}/>
                    <button onClick={()=>setNu(p=>({...p,subscriptionExpiry:p.subscriptionExpiry==="permanent"?"":"permanent"}))} style={{ padding:"10px 14px", borderRadius:8, border:`1px solid ${nu.subscriptionExpiry==="permanent"?C.green:C.border}`, background:nu.subscriptionExpiry==="permanent"?C.greenL:C.white, color:nu.subscriptionExpiry==="permanent"?C.green:C.slate, fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>
                      {nu.subscriptionExpiry==="permanent"?"✓ "+t.permanent:t.setPermanent}
                    </button>
                  </div>
                </div>
                <div><label style={LBL}>{t.role}</label>
                  <select value={nu.role} onChange={e=>setNu(p=>({...p,role:e.target.value}))} style={INP}>
                    <option value="user">{t.roleUser}</option><option value="admin">{t.roleAdmin}</option>
                  </select>
                </div>
                <div>
                  <label style={LBL}>{t.maxDevices}</label>
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <input
                      type="number" min="1" max="999"
                      value={nu.maxDevices===99?99:nu.maxDevices||3}
                      onChange={e=>setNu(p=>({...p,maxDevices:parseInt(e.target.value)||3}))}
                      disabled={nu.maxDevices===99}
                      style={{...INP, flex:1, opacity:nu.maxDevices===99?0.5:1}}
                    />
                    <button
                      onClick={()=>setNu(p=>({...p,maxDevices:p.maxDevices===99?3:99}))}
                      style={{ padding:"10px 12px", borderRadius:8, border:`1px solid ${nu.maxDevices===99?C.green:C.border}`, background:nu.maxDevices===99?C.greenL:C.white, color:nu.maxDevices===99?C.green:C.slate, fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}
                    >
                      {nu.maxDevices===99?"✓ Unlimited":"∞ Unlimited"}
                    </button>
                  </div>
                </div>
              </div>
              <div style={{ display:"flex", gap:10, marginTop:14 }}>
                <button onClick={addUser} disabled={saving} style={{ padding:"8px 18px", background:C.navy, color:C.white, border:"none", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", opacity:saving?0.6:1 }}>{saving?"Saving…":t.createAccount}</button>
                <button onClick={()=>setShowAdd(false)} style={{ padding:"8px 18px", background:C.offwhite, color:C.slate, border:`1px solid ${C.border}`, borderRadius:8, fontSize:12, cursor:"pointer" }}>{t.cancel}</button>
              </div>
            </div>
          )}

          {/* EDIT ACCOUNT FORM */}
          {editId&&(
            <div style={{ background:C.white, borderRadius:12, padding:22, border:`2px solid ${C.navy}`, marginBottom:16 }}>
              <div style={{ fontSize:14, fontWeight:700, marginBottom:14, color:C.navy }}>{t.editAccount}</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                {[["name",t.fullName],["email","Email"],["company",t.company]].map(([k,l])=>(
                  <div key={k}>
                    <label style={LBL}>{l}</label>
                    <input value={eu[k]||""} onChange={e=>setEu(p=>({...p,[k]:e.target.value}))} style={INP}/>
                  </div>
                ))}
                <div style={{ gridColumn:"1/-1" }}>
                  <label style={LBL}>{t.expiry}</label>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <input value={eu.subscriptionExpiry==="permanent"?"":eu.subscriptionExpiry||""} onChange={e=>setEu(p=>({...p,subscriptionExpiry:e.target.value}))} placeholder="YYYY-MM-DD" disabled={eu.subscriptionExpiry==="permanent"} style={{...INP,flex:1,opacity:eu.subscriptionExpiry==="permanent"?0.4:1}}/>
                    <button onClick={()=>setEu(p=>({...p,subscriptionExpiry:p.subscriptionExpiry==="permanent"?"":"permanent"}))} style={{ padding:"10px 14px", borderRadius:8, border:`1px solid ${eu.subscriptionExpiry==="permanent"?C.green:C.border}`, background:eu.subscriptionExpiry==="permanent"?C.greenL:C.white, color:eu.subscriptionExpiry==="permanent"?C.green:C.slate, fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>
                      {eu.subscriptionExpiry==="permanent"?"✓ "+t.permanent:t.setPermanent}
                    </button>
                  </div>
                </div>
                <div><label style={LBL}>{t.role}</label>
                  <select value={eu.role||"user"} onChange={e=>setEu(p=>({...p,role:e.target.value}))} style={INP}>
                    <option value="user">{t.roleUser}</option><option value="admin">{t.roleAdmin}</option>
                  </select>
                </div>
                <div><label style={LBL}>Status</label>
                  <select value={eu.subscriptionStatus||"active"} onChange={e=>setEu(p=>({...p,subscriptionStatus:e.target.value}))} style={INP}>
                    <option value="active">{t.active}</option>
                    <option value="suspended">{t.suspend}</option>
                  </select>
                </div>
                <div>
                  <label style={LBL}>{t.maxDevices}</label>
                  <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                    <input
                      type="number" min="1" max="999"
                      value={eu.maxDevices===99?99:eu.maxDevices||3}
                      onChange={e=>setEu(p=>({...p,maxDevices:parseInt(e.target.value)||3}))}
                      disabled={eu.maxDevices===99}
                      style={{...INP, flex:1, opacity:eu.maxDevices===99?0.5:1}}
                    />
                    <button
                      onClick={()=>setEu(p=>({...p,maxDevices:p.maxDevices===99?3:99}))}
                      style={{ padding:"10px 12px", borderRadius:8, border:`1px solid ${eu.maxDevices===99?C.green:C.border}`, background:eu.maxDevices===99?C.greenL:C.white, color:eu.maxDevices===99?C.green:C.slate, fontSize:11, fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}
                    >
                      {eu.maxDevices===99?"✓ Unlimited":"∞ Unlimited"}
                    </button>
                  </div>
                </div>
              </div>
              <div style={{ display:"flex", gap:10, marginTop:14 }}>
                <button onClick={saveEdit} disabled={saving} style={{ padding:"8px 18px", background:C.navy, color:C.white, border:"none", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer", opacity:saving?0.6:1 }}>{saving?"Saving…":t.saveChanges}</button>
                <button onClick={cancelEdit} style={{ padding:"8px 18px", background:C.offwhite, color:C.slate, border:`1px solid ${C.border}`, borderRadius:8, fontSize:12, cursor:"pointer" }}>{t.cancel}</button>
              </div>
            </div>
          )}

          {/* ACCOUNTS TABLE */}
          {loading ? <div style={{ padding:40, textAlign:"center", color:C.slateL }}>Loading accounts…</div> : (
          <div style={{ background:C.white, borderRadius:14, border:`1px solid ${C.border}`, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead>
                <tr style={{ background:C.offwhite }}>
                  {[t.user,t.companyCol,t.roleCol,t.statusCol,t.expiryCol,t.maxDevices,t.actionsCol].map(h=>(
                    <th key={h} style={{ padding:"10px 16px", textAlign:"left", fontWeight:700, color:C.slate, borderBottom:`2px solid ${C.border}`, fontSize:10, letterSpacing:"0.06em", textTransform:"uppercase", whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u,i)=>(
                  <tr key={u.id} style={{ borderBottom:`1px solid ${C.border}`, background:editId===u.id?"#EEF2FF":i%2===0?C.white:C.offwhite }}>
                    <td style={{ padding:"12px 16px" }}>
                      <div style={{ fontWeight:600, color:C.text }}>{u.name}</div>
                      <div style={{ fontSize:10, color:C.slateL }}>{u.email}</div>
                    </td>
                    <td style={{ padding:"12px 16px", color:C.textM }}>{u.company}</td>
                    <td style={{ padding:"12px 16px" }}>
                      <span style={{ fontSize:10, fontWeight:700, padding:"2px 9px", borderRadius:20, background:u.role==="admin"?"#EEF2FF":C.offwhite, color:u.role==="admin"?C.navy:C.slate, textTransform:"uppercase" }}>
                        {u.role==="admin"?t.roleAdmin:t.roleUser}
                      </span>
                    </td>
                    <td style={{ padding:"12px 16px" }}>
                      <span style={{ fontSize:10, fontWeight:700, padding:"2px 9px", borderRadius:20, background:u.subscriptionStatus==="active"?C.greenL:C.redL, color:u.subscriptionStatus==="active"?C.green:C.red, textTransform:"uppercase" }}>
                        {u.subscriptionStatus==="active"?t.active:t.suspend}
                      </span>
                    </td>
                    <td style={{ padding:"12px 16px", fontSize:11 }}>
                      {u.subscriptionExpiry
                        ? <span style={{ fontFamily:"monospace", color:C.textM }}>{u.subscriptionExpiry}</span>
                        : <span style={{ background:C.greenL, color:C.green, fontWeight:700, fontSize:10, padding:"2px 9px", borderRadius:20 }}>{t.neverExpires}</span>
                      }
                    </td>
                    <td style={{ padding:"12px 16px" }}>
                      {u.maxDevices===99
                        ? <span style={{ fontSize:10, fontWeight:700, background:C.greenL, color:C.green, padding:"2px 9px", borderRadius:20 }}>∞ Unlimited</span>
                        : <span style={{ fontSize:11, fontWeight:700, color:C.navy }}>{u.maxDevices||3} 📱</span>
                      }
                    </td>
                    <td style={{ padding:"10px 16px" }}>
                      <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                        {u.role!=="admin"&&(
                          <button onClick={()=>toggleSuspend(u)} style={{ padding:"4px 10px", borderRadius:6, border:"none", cursor:"pointer", fontSize:10, fontWeight:700, background:u.subscriptionStatus==="active"?C.amberL:C.greenL, color:u.subscriptionStatus==="active"?C.amber:C.green }}>
                            {u.subscriptionStatus==="active"?t.suspend:t.restore}
                          </button>
                        )}
                        <button onClick={()=>startEdit(u)} style={{ padding:"4px 10px", borderRadius:6, border:`1px solid ${C.navy}`, cursor:"pointer", fontSize:10, fontWeight:700, background:editId===u.id?C.navy:C.white, color:editId===u.id?C.white:C.navy }}>
                          ✏️ {t.edit}
                        </button>
                        {u.role!=="admin"&&(
                          <button onClick={()=>removeUser(u.id)} style={{ padding:"4px 10px", borderRadius:6, border:"none", cursor:"pointer", fontSize:10, fontWeight:700, background:C.redL, color:C.red }}>
                            🗑 {t.remove}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </>
      )}

      {tab==="gateways"&&(
        <div style={{ background:C.white, borderRadius:14, border:`1px solid ${C.border}`, overflow:"hidden" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead><tr style={{ background:C.offwhite }}>{[t.gatewayCol,t.sceneNameCol,t.deviceCodeCol,t.idCol,t.statusGwCol].map(h=><th key={h} style={{ padding:"10px 16px", textAlign:"left", fontWeight:700, color:C.slate, borderBottom:`2px solid ${C.border}`, fontSize:10, letterSpacing:"0.06em", textTransform:"uppercase", whiteSpace:"nowrap" }}>{h}</th>)}</tr></thead>
            <tbody>{gateways.map((gw,i)=>(
              <tr key={gw.id} style={{ borderBottom:`1px solid ${C.border}`, background:i%2===0?C.white:C.offwhite }}>
                <td style={{ padding:"12px 16px", fontWeight:600, color:C.text }}>{gw.name}</td>
                <td style={{ padding:"12px 16px", color:C.textM }}>{gw.scene_name||"—"}</td>
                <td style={{ padding:"12px 16px", fontFamily:"monospace", fontSize:11, color:C.slate }}>{gw.device_code}</td>
                <td style={{ padding:"12px 16px", fontFamily:"monospace", fontSize:11, color:C.slate }}>{gw.id}</td>
                <td style={{ padding:"12px 16px" }}><Dot online={gw.data?.iot_node_status==="online"} t={t}/></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {tab==="scenes"&&(
        <div style={{ background:C.white, borderRadius:14, border:`1px solid ${C.border}`, overflow:"hidden" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead><tr style={{ background:C.offwhite }}>{[t.sceneNameTbl,t.descriptionCol,t.idCol].map(h=><th key={h} style={{ padding:"10px 16px", textAlign:"left", fontWeight:700, color:C.slate, borderBottom:`2px solid ${C.border}`, fontSize:10, letterSpacing:"0.06em", textTransform:"uppercase" }}>{h}</th>)}</tr></thead>
            <tbody>{scenes.map((s,i)=>(
              <tr key={s.id} style={{ borderBottom:`1px solid ${C.border}`, background:i%2===0?C.white:C.offwhite }}>
                <td style={{ padding:"12px 16px", fontWeight:600, color:C.text }}>{s.name}</td>
                <td style={{ padding:"12px 16px", color:C.textM }}>{s.description||"—"}</td>
                <td style={{ padding:"12px 16px", fontFamily:"monospace", fontSize:11, color:C.slate }}>{s.id}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {tab==="sessions"&&<AdminSessionsView users={users} t={t}/>}
    </div>
  );
}

// ─── LOGIN PAGE ───────────────────────────────────────────────────────────────
function LoginPage({ onLogin, lang, setLang }) {
  const t = T[lang];
  const [email,setEmail]=useState(""); const [pass,setPass]=useState("");
  const [err,setErr]=useState(""); const [loading,setLoading]=useState(false);
  const [cap,setCap]=useState(""); const [capOk,setCapOk]=useState(false);
  const [kickedDevice,setKickedDevice]=useState(null);
  const code = useRef((() => { const ch="ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; return Array.from({length:5},()=>ch[Math.floor(Math.random()*ch.length)]).join(""); })()).current;

  const submit=async()=>{
    if(!capOk){ setErr(t.completeCaptcha); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password: pass }),
      });
      const data = await res.json();
      if (!res.ok) {
        setErr(data.error || t.invalidCredentials);
        setLoading(false);
        return;
      }
      // Store token in memory for subsequent API calls
      window._midlannexToken = data.token;
      // Persist token so login survives page refresh
      try { localStorage.setItem("mlx_token", data.token); localStorage.setItem("mlx_user", JSON.stringify(data.user)); } catch(e) {}
      // Show kicked device notice if applicable
      if(data.kickedSession) {
        setKickedDevice(data.kickedSession);
      } else {
        onLogin(data.user);
      }
      if(data.kickedSession) {
        // Store pending user to login after dismissing notice
        window._pendingLogin = data.user;
      }
    } catch(e) {
      setErr(t.invalidCredentials);
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight:"100vh", background:C.offwhite, fontFamily:"'Inter',-apple-system,sans-serif" }}>
      {/* Top bar: logo + lang switch */}
      <div style={{ background:C.white, borderBottom:`1px solid ${C.border}`, padding:"0 20px", height:56, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <Logo size={32}/>
          <div>
            <div style={{ fontSize:13, fontWeight:800, color:C.navy, letterSpacing:"0.02em" }}>{t.companyShort}</div>
            <div style={{ fontSize:9, color:C.slate, letterSpacing:"0.1em" }}>{t.companySub}</div>
          </div>
        </div>
        <LangSwitch lang={lang} setLang={setLang}/>
      </div>

      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"calc(100vh - 56px)", padding:"20px 20px" }}>
      <div style={{ width:"100%", maxWidth:420 }}>
        <div style={{ textAlign:"center", marginBottom:28 }}>
          {/* Large logo for login card */}
          <div style={{ display:"flex", justifyContent:"center", marginBottom:14 }}>
            <Logo size={72}/>
          </div>
          <div style={{ fontSize:12, color:C.slate }}>{t.platformName}</div>
        </div>

        {/* Kicked device notice */}
        {kickedDevice && (
          <div style={{ background:C.amberL, border:`1px solid #FDE68A`, borderRadius:12, padding:"18px 20px", marginBottom:16 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
              <span style={{ fontSize:20 }}>📱</span>
              <div style={{ fontWeight:700, fontSize:14, color:"#92400E" }}>{t.deviceKicked}</div>
            </div>
            <div style={{ fontSize:12, color:"#78350F", marginBottom:12, lineHeight:1.6 }}>{t.deviceKickedSub}</div>
            <div style={{ fontSize:11, color:"#92400E", background:"rgba(0,0,0,0.06)", borderRadius:6, padding:"8px 10px", marginBottom:12 }}>
              <strong>{kickedDevice.deviceName}</strong> · {t.sessionLastSeen}: {kickedDevice.lastSeen}
            </div>
            <button onClick={()=>{ setKickedDevice(null); onLogin(window._pendingLogin); }} style={{ padding:"8px 20px", background:"#92400E", color:C.white, border:"none", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer" }}>
              {t.understood} →
            </button>
          </div>
        )}

        <div style={{ background:C.white, borderRadius:16, padding:"36px 32px", boxShadow:"0 4px 32px rgba(0,0,0,0.08)", border:`1px solid ${C.border}` }}>
          <h2 style={{ margin:"0 0 4px", fontSize:21, fontWeight:700, color:C.text }}>{t.signIn}</h2>
          <p style={{ margin:"0 0 24px", fontSize:12, color:C.slate }}>{t.signInSub}</p>
          {err&&<div style={{ background:C.redL, border:`1px solid #FECACA`, borderRadius:8, padding:"9px 13px", marginBottom:16, fontSize:12, color:C.red, fontWeight:600 }}>{err}</div>}
          <label style={LBL}>{t.emailLabel}</label>
          <input value={email} onChange={e=>setEmail(e.target.value)} placeholder={t.emailPlaceholder} style={{...INP,marginBottom:14}} type="email"/>
          <label style={LBL}>{t.passwordLabel}</label>
          <input value={pass} onChange={e=>setPass(e.target.value)} placeholder="••••••••" style={{...INP,marginBottom:14}} type="password" onKeyDown={e=>e.key==="Enter"&&submit()}/>
          <label style={LBL}>{t.securityLabel}</label>
          <div style={{ display:"flex", gap:10, marginBottom:capOk?6:14, alignItems:"center" }}>
            <div style={{ background:`linear-gradient(135deg,${C.navy},${C.navyL})`, borderRadius:8, padding:"9px 16px", letterSpacing:"0.3em", fontSize:18, fontWeight:800, color:C.white, fontFamily:"monospace", userSelect:"none", minWidth:110, textAlign:"center" }}>{code}</div>
            <input value={cap} onChange={e=>{ const v=e.target.value.toUpperCase(); setCap(v); setCapOk(v===code); }} placeholder={t.captchaPlaceholder} style={{...INP,flex:1,letterSpacing:"0.15em",fontFamily:"monospace",marginBottom:0}} maxLength={5}/>
          </div>
          {capOk&&<div style={{ fontSize:11, color:C.green, fontWeight:700, marginBottom:14 }}>{t.captchaVerified}</div>}
          <button onClick={submit} style={{ width:"100%", padding:"12px", background:loading?C.slateL:C.navy, color:C.white, border:"none", borderRadius:10, fontSize:14, fontWeight:700, cursor:loading?"not-allowed":"pointer", marginTop:2 }}>
            {loading?t.signingIn:t.signIn}
          </button>
          {/* LINE contact */}
          <a href="https://lin.ee/pxYlIH3" target="_blank" rel="noreferrer" style={{
            display:"flex", alignItems:"center", justifyContent:"center", gap:8,
            marginTop:14, padding:"10px", background:"#06C755",
            color:C.white, borderRadius:9, fontSize:12, fontWeight:700,
            textDecoration:"none", letterSpacing:"0.02em",
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.105.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/></svg>
            {t.lineSupportFull}
          </a>
          <div style={{ marginTop:12, textAlign:"center" }}>
            <span style={{ fontSize:11, color:C.slateL }}>{t.footerPrivacy}</span>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign:"center", marginTop:24, paddingBottom:16 }}>
          <div style={{ fontSize:11, color:C.slateL, marginBottom:4 }}>{t.copyright}</div>
          <div style={{ fontSize:10, color:C.slateL, marginBottom:8 }}>{t.footerTagline}</div>
          <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:16, flexWrap:"wrap" }}>
            <a href="https://midlannex.co.th" target="_blank" rel="noreferrer" style={{ fontSize:10, color:C.slate, textDecoration:"none" }}>🌐 {t.website}</a>
            <a href="mailto:report@midlannex.co.th" style={{ fontSize:10, color:C.slate, textDecoration:"none" }}>✉️ {t.contactEmail}</a>
            <a href="https://lin.ee/pxYlIH3" target="_blank" rel="noreferrer" style={{ fontSize:10, color:C.slate, textDecoration:"none" }}>💬 LINE</a>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

// ─── SUSPENDED PAGE ───────────────────────────────────────────────────────────
function SuspendedPage({ user, onLogout, lang, setLang }) {
  const t = T[lang];
  return (
    <div style={{ minHeight:"100vh", background:C.offwhite, fontFamily:"'Inter',-apple-system,sans-serif" }}>
      {/* Top bar */}
      <div style={{ background:C.white, borderBottom:`1px solid ${C.border}`, padding:"0 20px", height:56, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <Logo size={32}/>
          <div>
            <div style={{ fontSize:13, fontWeight:800, color:C.navy }}>{t.companyShort}</div>
            <div style={{ fontSize:9, color:C.slate, letterSpacing:"0.1em" }}>{t.companySub}</div>
          </div>
        </div>
        <LangSwitch lang={lang} setLang={setLang}/>
      </div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"calc(100vh - 56px)", padding:"20px" }}>
      <div style={{ maxWidth:520, width:"100%", padding:"0 20px" }}>
        <div style={{ background:C.white, borderRadius:16, padding:"44px 40px", boxShadow:"0 4px 32px rgba(0,0,0,0.08)", textAlign:"center" }}>

          <div style={{ width:54, height:54, borderRadius:"50%", background:C.redL, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 18px", fontSize:24 }}>🔒</div>
          <h2 style={{ margin:"0 0 12px", fontSize:20, fontWeight:700, color:C.text }}>{t.suspendedTitle}</h2>
          <p style={{ margin:"0 0 22px", fontSize:13, color:C.textM, lineHeight:1.8 }}>
            {lang==="th"
              ? <>เรียน <strong>{user.name}</strong><br/><br/>{t.suspendedBody1} <strong>{user.company}</strong> {t.suspendedBody2}<br/><br/>{t.suspendedBody3}</>
              : <>Dear <strong>{user.name}</strong>,<br/><br/>{t.suspendedBody1} <strong>{user.company}</strong> {t.suspendedBody2}<br/><br/>{t.suspendedBody3}</>
            }
          </p>
          <div style={{ background:C.amberL, border:`1px solid #FDE68A`, borderRadius:10, padding:"12px 16px", marginBottom:22, textAlign:"left" }}>
            <div style={{ fontSize:11, fontWeight:800, color:"#92400E", letterSpacing:"0.06em", marginBottom:3 }}>{t.subscriptionExpired}</div>
            <div style={{ fontSize:12, color:"#78350F" }}>{t.expired} {user.subscriptionExpiry}</div>
          </div>
          <a href="https://lin.ee/pxYlIH3" target="_blank" rel="noreferrer" style={{ display:"block", padding:"12px", background:"#06C755", color:C.white, borderRadius:10, fontSize:13, fontWeight:700, textDecoration:"none", marginBottom:10 }}>💬 {t.contactLine}</a>
          <button onClick={onLogout} style={{ background:"none", border:"none", color:C.slate, fontSize:12, cursor:"pointer" }}>← {t.signOut}</button>
        </div>
      </div>
      </div>
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [lang, setLang] = useState("en");
  const [restoringSession, setRestoringSession] = useState(true);

  // Prevent mobile zoom
  useEffect(() => {
    const meta = document.querySelector("meta[name=viewport]");
    if (meta) meta.setAttribute("content", "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no");
  }, []);

  // Restore session from localStorage on page load/refresh
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("mlx_token");
        const savedUser = localStorage.getItem("mlx_user");
        if (token && savedUser) {
          // Verify token is still valid with backend
          window._midlannexToken = token;
          const r = await fetch(`${API_BASE}/auth/me`, {
            headers: { "Authorization": `Bearer ${token}` }
          });
          if (r.ok) {
            const freshUser = await r.json();
            setUser({
              id: freshUser.id,
              email: freshUser.email,
              role: freshUser.role,
              name: freshUser.name,
              company: freshUser.company,
              subscriptionStatus: freshUser.subscription_status,
              subscriptionExpiry: freshUser.subscription_expiry,
              sceneIds: freshUser.scene_ids || [],
              maxDevices: freshUser.max_devices || 3,
            });
          } else {
            // Token expired — clear storage
            localStorage.removeItem("mlx_token");
            localStorage.removeItem("mlx_user");
            window._midlannexToken = null;
          }
        }
      } catch(e) {
        console.warn("Session restore failed:", e.message);
      }
      setRestoringSession(false);
    })();
  }, []);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("mlx_token");
      if (token) {
        await fetch(`${API_BASE}/auth/logout`, {
          method: "POST",
          headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" }
        });
      }
    } catch(e) {}
    localStorage.removeItem("mlx_token");
    localStorage.removeItem("mlx_user");
    window._midlannexToken = null;
    setUser(null);
  };

  if (restoringSession) return (
    <div style={{ minHeight:"100vh", background:C.offwhite, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Inter',-apple-system,sans-serif" }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ width:40, height:40, border:`3px solid ${C.border}`, borderTop:`3px solid ${C.navy}`, borderRadius:"50%", animation:"spin 0.8s linear infinite", margin:"0 auto 14px" }}/>
        <div style={{ fontSize:13, color:C.slate }}>Restoring session...</div>
        <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
      </div>
    </div>
  );

  if (!user) return <LoginPage onLogin={setUser} lang={lang} setLang={setLang}/>;
  if (user.subscriptionStatus==="suspended") return <SuspendedPage user={user} onLogout={handleLogout} lang={lang} setLang={setLang}/>;
  return <Dashboard user={user} onLogout={handleLogout} lang={lang} setLang={setLang}/>;
}
